import React, { useState, useEffect, useRef, createElement, Fragment, useCallback } from "react";
// Icons removed as requested
import { CheckIsEmptyArray } from '../Common';
import ReactMarkdown from 'react-markdown';
// NOTE: remark-gfm is intentionally NOT imported. remark-gfm@4 is ESM-only and
// pulls in modern micromark/mdast deps (numeric separators, 'devlop') that this
// CRA4/webpack4/babel toolchain cannot parse (build breaks). Markdown tables are
// instead rendered by the local splitMarkdownContent() parser below, which
// extracts pipe-tables into <table> blocks before ReactMarkdown sees them.
import './Chatbot.scss'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

// Interactive chart renderer (recharts) for the ```chart spec``` blocks emitted by the
// data-analysis tools. Hover = tooltip; click (in-bubble) = open full-screen.
const DA_PALETTE = ['#1963ae', '#2dd4bf', '#f59e0b', '#8b5cf6', '#ef4444', '#10b981', '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16', '#a855f7', '#6366f1'];
// Memoized chart wrapper. A finished message's chart JSON (content) is stable, so
// React.memo prevents re-render when a LATER message streams and re-renders the
// message list -- otherwise JSON.parse reruns, the spec object is recreated, and
// recharts' ResponsiveContainer is torn down mid-stream, making previously-rendered
// charts flicker / flash empty while the next reply comes in.
const ChartBlock = React.memo(function ChartBlock({ content, openLightbox }) {
    let spec;
    try { spec = JSON.parse(content); } catch (e) { return null; }
    return <DAChart spec={spec} onChartClick={openLightbox ? () => openLightbox(spec) : undefined} />;
});

// Interactive carrier/lane widget: a Carrier<->Lane toggle that switches BOTH a
// sortable table and a bar chart together. Rendered by DAChart for spec.type ===
// 'carrier_lane' (emitted by analyze_carrier_performance, which returns both views).
function CarrierLaneWidget({ spec, fullscreen, onChartClick }) {
    const viewNames = Object.keys(spec.views || {});
    const typeColor = (t) => DA_PALETTE[Math.max(0, viewNames.indexOf(t)) % DA_PALETTE.length];
    const dimTotal = {};
    viewNames.forEach(v => { const vd = spec.views[v] || { total: 0 }; dimTotal[v] = (typeof vd.total === 'number') ? vd.total : 0; });
    const fmt = (v) => (typeof v === 'number' ? v.toLocaleString() : v);
    const matrix = spec.matrix || null;
    const [tab, setTab] = useState((spec.default === 'matrix' || viewNames.indexOf(spec.default) < 0) ? 'matrix' : spec.default);
    const rowsFor = (v) => {
        const vd = spec.views[v] || { labels: [], values: [] };
        return (vd.labels || []).map((l, i) => ({ name: String(l), value: Number(vd.values ? vd.values[i] : 0) || 0 }));
    };
    const singleRows = (tab !== 'matrix') ? rowsFor(tab).sort((a, b) => b.value - a.value) : [];
    const pct = (val, dim) => (dimTotal[dim] ? ((val / dimTotal[dim] * 100).toFixed(1) + '%') : '0.0%');
    return (
        <div className={'cl-widget' + (fullscreen ? ' cl-widget--fullscreen' : '')}>
            <div className="cl-widget__head">
                {spec.title && <div className="cl-widget__title">{spec.title}</div>}
                <div className="cl-tabs" role="tablist">
                    <button type="button" role="tab" className={'cl-tabs__btn' + (tab === 'matrix' ? ' is-active' : '')} onClick={() => setTab('matrix')} aria-selected={tab === 'matrix'}>Heatmap</button>
                    {viewNames.map(v => (
                        <button key={v} type="button" role="tab" className={'cl-tabs__btn' + (tab === v ? ' is-active' : '')} onClick={() => setTab(v)} aria-selected={tab === v}>{v}</button>
                    ))}
                </div>
            </div>

            {tab === 'matrix' && matrix ? (
                <div className="cl-heat">
                    <div className="cl-heat__legend">
                        <span>{matrix.valueKey} per {matrix.colKey.toLowerCase()} x {matrix.rowKey.toLowerCase()}</span>
                        <span className="cl-heat__bar" />
                        <span>0 - {fmt(matrix.max)}</span>
                    </div>
                    <div className={'cl-heat__scroll' + (fullscreen ? ' cl-heat__scroll--fullscreen' : '')}>
                        <table className="cl-heat__table">
                            <thead>
                                <tr>
                                    <th className="cl-heat__corner">{matrix.rowKey} \ {matrix.colKey}</th>
                                    {matrix.cols.map(c => <th key={c} className="cl-heat__colh">{c}</th>)}
                                    <th className="cl-heat__tot">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matrix.rows.map((lane, i) => (
                                    <tr key={lane}>
                                        <th className="cl-heat__rowh">{lane}</th>
                                        {matrix.cells[i].map((val, j) => {
                                            const a = matrix.max ? (val / matrix.max) : 0;
                                            return (
                                                <td key={j} className="cl-heat__cell" style={{ background: val ? ('rgba(25, 99, 174, ' + (0.1 + 0.8 * a).toFixed(2) + ')') : 'var(--cb-surface-3)', color: a > 0.5 ? '#fff' : 'var(--cb-ink)' }} title={lane + ' x ' + matrix.cols[j] + ': ' + fmt(val)}>
                                                    {val ? fmt(val) : ''}
                                                </td>
                                            );
                                        })}
                                        <td className="cl-heat__tot">{fmt(matrix.rowTotals[i])}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th className="cl-heat__rowh">Total</th>
                                    {matrix.colTotals.map((t, j) => <td key={j} className="cl-heat__tot">{fmt(t)}</td>)}
                                    <td className="cl-heat__tot cl-heat__grand">{fmt(matrix.total)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}

            {tab !== 'matrix' && (
                <>
                    <div className="cl-widget__tablewrap">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th className="cl-table__num">#</th>
                                    <th>{tab}</th>
                                    <th className="cl-table__num">Bookings</th>
                                    <th className="cl-table__num">Share</th>
                                </tr>
                            </thead>
                            <tbody>
                                {singleRows.map((r, i) => (
                                    <tr key={r.name}>
                                        <td className="cl-table__num">{i + 1}</td>
                                        <td>{r.name}</td>
                                        <td className="cl-table__num">{fmt(r.value)}</td>
                                        <td className="cl-table__num">{pct(r.value, tab)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className={'da-rechart' + (fullscreen ? ' da-rechart-fullscreen' : '')} onClick={!fullscreen && onChartClick ? onChartClick : undefined}>
                        <div className="da-rechart-title">{tab}</div>
                        <ResponsiveContainer width="100%" height={fullscreen ? 460 : 300}>
                            <BarChart data={singleRows} margin={{ top: 8, right: 16, left: 4, bottom: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f4" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} height={64} />
                                <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} />
                                <Tooltip formatter={fmt} />
                                <Bar dataKey="value" radius={[4, 4, 0, 0]}>{singleRows.map((_, i) => <Cell key={i} fill={typeColor(tab)} />)}</Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </>
            )}
        </div>
    );
}

function DAChart({ spec, fullscreen, onChartClick }) {
    if (spec && spec.type === 'carrier_lane') {
        return <CarrierLaneWidget spec={spec} fullscreen={fullscreen} onChartClick={onChartClick} />;
    }
    // Empty data (e.g. a 0-result analysis) would draw a chart shell with axes but
    // no bars — render nothing instead. The server also short-circuits these, but
    // guard here too so any empty spec stays clean.
    if (!spec || !Array.isArray(spec.labels) || !Array.isArray(spec.values) || spec.values.length === 0) return null;
    const data = spec.labels.map((l, i) => ({ name: String(l), value: Number(spec.values[i]) || 0 }));
    const title = spec.title || '';
    const fmt = (v) => (typeof v === 'number' ? v.toLocaleString() : v);
    const cells = data.map((_, i) => <Cell key={i} fill={DA_PALETTE[i % DA_PALETTE.length]} />);
    const wrap = (content, h) => (
        <div className={'da-rechart' + (fullscreen ? ' da-rechart-fullscreen' : '')} onClick={!fullscreen && onChartClick ? onChartClick : undefined}>
            {title && <div className="da-rechart-title">{title}</div>}
            <ResponsiveContainer width="100%" height={h}>{content}</ResponsiveContainer>
        </div>
    );
    if (spec.type === 'pie') {
        return wrap((
            <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={fullscreen ? 180 : 90}
                     label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {cells}
                </Pie>
                <Tooltip formatter={fmt} />
                <Legend />
            </PieChart>
        ), fullscreen ? 440 : 300);
    }
    const horizontal = spec.type === 'barh';
    const height = fullscreen ? 500 : (horizontal ? Math.max(220, data.length * 42 + 30) : 300);
    if (horizontal) {
        return wrap((
            <BarChart data={data} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmt} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={fullscreen ? 170 : 140} />
                <Tooltip formatter={fmt} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>{cells}</Bar>
            </BarChart>
        ), height);
    }
    return wrap((
        <BarChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f4" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} height={64} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={fmt} />
            <Tooltip formatter={fmt} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>{cells}</Bar>
        </BarChart>
    ), height);
}
// >>> LANGGRAPH CHATBOT INTEGRATION — BMS Navigation Tools >>>
import { executeTool, ACTION_VERIFY_BUTTONS } from './bmsTools';
// <<< LANGGRAPH CHATBOT INTEGRATION — BMS Navigation Tools <<<


// >>> Chatbot role gate (mirrors backend auth_middleware CHATBOT_ALLOWED_ROLES) >>>
// The chatbot only SHOWS for these profile_type_id values (the JWT 'profile' claim in the
// id_token). UX layer only - the backend gate (403) is authoritative. Keep in sync with
// app_external/.env CHATBOT_ALLOWED_ROLES. OP = M+R Operations (incl. consolidation work).
//const CHATBOT_ALLOWED_PROFILES = ['OP', 'ADMIN'];
//function getChatbotProfileClaim() {
//    try {
//        const tok = localStorage.getItem('id_token');
//        if (!tok) return null;
//        const part = tok.split('.')[1];
//        if (!part) return null;
//        const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
//        return payload.profile || null;
//    } catch (e) {
//        return null;
//    }
//}
//function isChatbotAllowedForCurrentUser() {
//    const p = (getChatbotProfileClaim() || '').trim().toUpperCase();
//    return !!p && CHATBOT_ALLOWED_PROFILES.some(x => x.toUpperCase() === p);
//}
// <<< Chatbot role gate >>>

// >>> Confirmation gate (Accept / Keep-edit / Reject) >>>
// Tools that PERSIST a change are intercepted in handleToolCall and shown to the
// user BEFORE the DOM mutation runs. Classification mirrors bmsTools.js so the
// gate and the executor's outcome-verification agree (ACTION_VERIFY_BUTTONS is
// imported from there - the single source of truth).
const PERSIST_TOOLS = new Set(['save_and_confirm', 'approve_booking']);
function isPersistToolCall(toolName, params) {
    const p = params || {};
    if (PERSIST_TOOLS.has(toolName)) return true;
    if (toolName === 'click_button') {
        return ACTION_VERIFY_BUTTONS.test(String(p.button_name || '').trim());
    }
    if (toolName === 'handle_popup_confirm') {
        const a = String(p.action || '').toLowerCase();
        return a === 'ok' || a === 'confirm' || a === 'yes' || Boolean(p.button_text);
    }
    if (toolName === 'batch_form_actions') {
        const acts = Array.isArray(p.actions) ? p.actions : [];
        return acts.some(function (ac) {
            if (!ac) return false;
            const ap = ac.params || ac;
            if (ac.action === 'click_button') return ACTION_VERIFY_BUTTONS.test(String(ap.button_name || '').trim());
            if (ac.action === 'handle_popup_confirm') return String(ap.action || '').toLowerCase() !== 'cancel';
            return false;
        });
    }
    return false;
}
function humanizePersist(toolName, params) {
    const p = params || {};
    if (toolName === 'approve_booking') return 'Approve shipping order ' + (p.so_number || '');
    if (toolName === 'save_and_confirm') return 'Save (' + (p.button_name || 'Save') + ')';
    if (toolName === 'handle_popup_confirm') return 'Confirm popup';
    if (toolName === 'batch_form_actions') return 'Run batch actions';
    if (toolName === 'click_button') return 'Click "' + (p.button_name || '') + '"';
    return toolName;
}
function rejectTextFor(toolName) {
    return 'RESULT: user_rejected -- the user rejected the ' + toolName
        + ' action at the confirmation gate. No changes were committed to the Shipping Order.'
        + ' Do NOT claim success, do NOT say Saved/Approved/Updated/Submitted, and do NOT quote any'
        + ' success toast. Acknowledge the decision in one short natural sentence, confirm the record'
        + ' is unchanged, and offer concrete alternatives. Do NOT silently retry the same commit unless'
        + ' the user explicitly asks.';
}
// Form-mutation tools whose values we capture during the turn so the user can
// edit them via Keep(edit) before the persist fires.
const FORM_MUTATION_TOOLS = new Set(['fill_form', 'select_option', 'set_date', 'toggle_checkbox', 'select_radio', 'fill_table_cell', 'select_proposed_sailing']);
function buildFormChangeEntries(toolName, params) {
    const p = params || {};
    const out = [];
    const id = (label, name, extra) => toolName + '|' + (label || name || extra || '');
    if (toolName === 'fill_form') {
        (Array.isArray(p.fields) ? p.fields : []).forEach(function (f) {
            out.push({ id: id(f.label, f.name), tool: 'fill_form', label: f.label || f.name || 'field', name: f.name || '', value: f.value, displayValue: f.value, type: 'text' });
        });
    } else if (toolName === 'select_option') {
        out.push({ id: id(p.label, p.name), tool: 'select_option', label: p.label || p.name || 'option', name: p.name || '', value: p.value, displayValue: p.value, type: 'text' });
    } else if (toolName === 'set_date') {
        out.push({ id: id(p.label, p.name), tool: 'set_date', label: p.label || p.name || 'date', name: p.name || '', value: p.value, displayValue: p.value, type: 'date' });
    } else if (toolName === 'toggle_checkbox') {
        out.push({ id: id(p.label, p.name), tool: 'toggle_checkbox', label: p.label || p.name || 'checkbox', name: p.name || '', value: p.checked, displayValue: p.checked ? 'on' : 'off', type: 'checkbox' });
    } else if (toolName === 'select_radio') {
        out.push({ id: id(p.label, p.name), tool: 'select_radio', label: p.label || p.name || 'radio', name: p.name || '', value: p.value, displayValue: p.value, type: 'radio' });
    } else if (toolName === 'fill_table_cell') {
        out.push({ id: id(p.column_name, null, p.row_identifier), tool: 'fill_table_cell', label: (p.column_name || '') + ' @ ' + (p.row_identifier || ''), name: '', value: p.value, displayValue: p.value, type: 'text', row_identifier: p.row_identifier, column_name: p.column_name });
    } else if (toolName === 'select_proposed_sailing') {
        out.push({ id: id('Approved Sailing'), tool: 'select_proposed_sailing', label: 'Approved Sailing', name: '', value: 'option ' + p.option_number, displayValue: 'option ' + p.option_number, type: 'text' });
    }
    return out;
}
// <<< Confirmation gate >>>

// Configuration - centralized for easy updates
const CONFIG = {
    // Auto-detect environment based on hostname
    isDevelopment: typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1'),

    MODE: 'azure',  // 'docker' uses localhost:8123, 'local' uses localhost:2024, 'azure' uses Azure Container App

    get mode() {
        return this.MODE;
    },

    // Azure Functions Key for production mode
    FUNCTIONS_KEY: localStorage.getItem('azure_functions_key') || '',

    // URLs based on mode
    get LANGGRAPH_URL() {
        if (!this.isDevelopment) {
            // Production: Use environment variable or default Azure URL
            return 'https://insight-chatbot-prod.ashyforest-6a63d8b2.eastus.azurecontainerapps.io';
        }

        // Manual azure override: use Azure Container App even in development
        if (this.mode === 'azure') {
            return 'https://insight-chatbot-prod.ashyforest-6a63d8b2.eastus.azurecontainerapps.io';
        }

        // Development: Use local or docker based on mode
        return this.mode === 'docker'
            ? 'http://localhost:8123'  // Docker LangGraph server
            : 'http://localhost:2024'; // Local LangGraph server
    },

    // Assistant ID
    ASSISTANT_ID: 'agent'
};

const LANGGRAPH_URL = CONFIG.LANGGRAPH_URL;
const ASSISTANT_ID = CONFIG.ASSISTANT_ID;
const CONNECTION_STATUS_COPY = {
    idle: 'Offline',
    connecting: 'Connecting to assistant...',
    online: 'Online',
    error: 'Offline - retrying...'
};

// Starter-prompt chips are fully backend-driven: the LIVE list is fetched on mount
// from ${LANGGRAPH_URL}/suggestions (served by the langgraph docker image). The
// backend can hide ALL chips by returning [] (CHATBOT_SUGGESTIONS_ENABLED=false in
// app_external/.env). Intentionally empty here too, so an unreachable backend also
// shows no chips — there is no hardcoded fallback chip anymore.
const DEFAULT_SUGGESTED_PROMPTS = [];

// Drops tool-noise phase entries ("Tool: read_so_tab", "Running navigate_to_page",
// "Running browser actions") so the conceptual reasoning flow reads cleanly in the
// expanded section. Tool steps are shown separately as the action trace.
const filterReasoningPhases = (phases) => (phases || []).filter((phase) => {
    if (!phase) return false;
    if (/^Tool:\s/i.test(phase)) return false;
    if (/^Running\b/i.test(phase)) return false;
    return true;
});

// One row in the expanded action trace. Holds its own expand/collapse state so
// per-item UI doesn't thread through the parent's collapsed-thinking set.
function ToolTraceItem({ tool }) {
    const [open, setOpen] = useState(false);
    const { tool_name, params, output_text, ok } = tool;

    let paramStr = '';
    try {
        const s = JSON.stringify(params);
        paramStr = s === '{}' ? '' : s;
    } catch (e) {
        paramStr = '';
    }

    const toggle = () => setOpen((o) => !o);

    return (
        <div className={`action-trace-item${ok ? '' : ' is-error'}`}>
            <div
                className="action-trace-item__header"
                onClick={toggle}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle();
                    }
                }}
            >
                <span className={`action-trace-item__marker${ok ? ' is-ok' : ' is-err'}`} aria-hidden="true">
                    {ok ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    )}
                </span>
                <span className="action-trace-item__name">{tool_name}</span>
                {paramStr && <span className="action-trace-item__params">{paramStr}</span>}
                <svg className={`action-trace-item__chevron${open ? ' is-open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </div>
            {open && (
                <div className="action-trace-item__body">
                    {paramStr && (
                        <div className="action-trace-item__paramsfull">
                            <span className="action-trace-item__label">input</span>
                            <pre>{paramStr}</pre>
                        </div>
                    )}
                    <div className="action-trace-item__result">
                        <span className="action-trace-item__label">{ok ? 'result' : 'error'}</span>
                        <pre>{output_text || '(no output)'}</pre>
                    </div>
                </div>
            )}
        </div>
    );
}


// One expandable reasoning-phase chip — same green-tick card style as a tool
// action (ToolTraceItem), so phases and actions read as one uniform trace.
// The knowledge-base phase expands to show the retrieved sources; other phases
// show a short description. When the deployment streams reasoning/thinking text
// (Kimi reasoning_content), the "Analyzing your question" phase surfaces it
// instead of the canned description — see getStreamReasoningContent.
const PHASE_DESCRIPTIONS = {
    'Analyzing your question': 'Parsed the request and chose how to handle it — retrieve from the manual, navigate the live site, or answer directly.',
    'Generating response': 'Composed and formatted the final answer from the information gathered above.',
};
function PhaseItem({ phase, sources, detail, markdown = false }) {
    const [open, setOpen] = useState(false);
    const isKB = !detail && /searching knowledge base|retriev/i.test(phase);
    const toggle = () => setOpen((o) => !o);
    const srcList = (sources || [])
        .filter((s) => s && (s.source || s.page_no))
        .map((s, i) => `${i + 1}. ${s.source || 'Unknown'}${s.page_no && s.page_no !== 'N/A' ? ` (p.${s.page_no})` : ''}`);
    const desc = detail || PHASE_DESCRIPTIONS[phase] || 'Processing step.';
    return (
        <div className='action-trace-item'>
            <div
                className='action-trace-item__header'
                onClick={toggle}
                role='button'
                tabIndex={0}
                aria-expanded={open}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
            >
                <span className='action-trace-item__marker is-ok' aria-hidden='true'>
                    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3'>
                        <polyline points='20 6 9 17 4 12' />
                    </svg>
                </span>
                <span className='action-trace-item__name'>{phase}</span>
                <svg className={`action-trace-item__chevron${open ? ' is-open' : ''}`} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                    <polyline points='6 9 12 15 18 9' />
                </svg>
            </div>
            {open && (
                <div className='action-trace-item__body'>
                    <div className='action-trace-item__result'>
                        <span className='action-trace-item__label'>{isKB ? 'sources' : (detail ? (markdown ? 'answer' : 'thinking') : 'detail')}</span>
                        {markdown
                            ? <div className='action-trace-item__markdown'><ReactMarkdown>{desc}</ReactMarkdown></div>
                            : <pre>{isKB ? (srcList.length ? srcList.join('\n') : '(no sources retrieved)') : desc}</pre>}
                    </div>
                </div>
            )}
        </div>
    );
}

// In-chat Accept / Keep(edit) / Reject confirmation card shown before a persist
// tool runs. Rendered as its own message bubble (kind === 'confirmation') so it
// never passes through ReactMarkdown.
function ConfirmationCard({ payload, edit, resolvedChoice, onAccept, onKeep, onReject, onApplyEdits, onCancelEdit }) {
    const title = humanizePersist(payload.tool_name, payload.params);
    const list = edit ? edit.items : (payload.prepared || []);
    const disabled = !!resolvedChoice;
    const [edits, setEdits] = useState({});
    const setItem = (id, v) => setEdits(prev => ({ ...prev, [id]: v }));
    const editedItems = edit
        ? list.map(it => ({ ...it, editValue: edits[it.id] !== undefined ? edits[it.id] : it.editValue }))
              .filter(it => String(it.editValue) !== String(it.value))
        : [];
    return (
        <div className={'confirm-card' + (resolvedChoice ? ' is-resolved' : '')}>
            <div className="confirm-card__title">{title}</div>
            <div className="confirm-card__body">
                {list.length > 0 ? (
                    <ul className="confirm-card__changes">
                        {list.map(c => (
                            <li key={c.id} className="confirm-card__change">
                                <span className="confirm-card__change-label">{c.label || c.name}</span>
                                {edit ? (
                                    c.type === 'checkbox'
                                      ? <select className="confirm-card__input" value={edits[c.id] !== undefined ? edits[c.id] : c.editValue} onChange={e => setItem(c.id, e.target.value === 'true')} disabled={disabled}>
                                            <option value={true}>on</option>
                                            <option value={false}>off</option>
                                        </select>
                                      : <input className="confirm-card__input" type={c.type === 'date' ? 'date' : c.type === 'number' ? 'number' : 'text'} value={edits[c.id] !== undefined ? edits[c.id] : c.editValue} onChange={e => setItem(c.id, e.target.value)} disabled={disabled} />
                                ) : (
                                    <span className="confirm-card__change-value">{String(c.displayValue != null ? c.displayValue : c.value)}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                ) : <span className="confirm-card__body-text">This will commit the record to BMS. Please review before proceeding.</span>}
            </div>
            <div className="confirm-card__actions">
                {!edit ? (
                    <>
                        <button type="button" className="confirm-btn confirm-btn--reject" disabled={disabled} onClick={onReject}>Reject</button>
                        <button type="button" className="confirm-btn confirm-btn--keep" disabled={disabled || list.length === 0} onClick={onKeep}>Keep (edit)</button>
                        <button type="button" className="confirm-btn confirm-btn--accept" disabled={disabled} onClick={onAccept}>Accept</button>
                    </>
                ) : (
                    <>
                        <button type="button" className="confirm-btn confirm-btn--keep" onClick={onCancelEdit}>Cancel</button>
                        <button type="button" className="confirm-btn confirm-btn--accept" onClick={() => onApplyEdits(editedItems)}>Apply &amp; keep</button>
                    </>
                )}
            </div>
        </div>
    );
}

export default function Chatbot(props) {
    let threadId = null;
    let isOpen = false;
    let streamingContent = '';
    let currentMessageId = null;
    let isDragging = false;
    let dragOffsetX = 0;
    let dragOffsetY = 0;
    let conversationHistory = []; // Track conversation for context
    // Keep bubble within viewport
    let maxX;
    let maxY;

    // DOM elements
    let chatBubble;
    let chatWindow;
    let closeChat;
    let chatForm;
    //let chatInput;
    let chatMessages;
    let sendButton;
    let statusContainer;
    let statusTextEl = null;
    let defaultInputPlaceholder = 'Type your message...';

    let isInitializingThread = false;
    let threadInitializationPromise = null;

    // Resize functionality
    let isResizing = false;
    let resizeDirection = '';
    let startX, startY, startWidth, startHeight, startLeft, startBottom;
    let offset = { left: 0, top: 0 };
    let last_mouse = { x: 0, y: 0 };

    const [chatMsg, setChatMsg] = useState([]);
    const [didMount, setDidMount] = useState(false);
    const [showProcessing, setShowProcessing] = useState(false);
    const [showTyping, setShowTyping] = useState(false);
    const [disableInput, setDisableInput] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const openChartLightbox = useCallback((spec) => setLightbox({ kind: 'chart', spec }), []);
    const [errorMsg, setErrorMsg] = useState('');
    const [selectedCitation, setSelectedCitation] = useState(null);
    const [showSourcePanel, setShowSourcePanel] = useState(false);
    const [imageEnlarged, setImageEnlarged] = useState(false);
    const inputRef = useRef('');
    const chatMsgRef = useRef(null);
    const processingStartTimeRef = useRef(null);
    const minProcessingDelayRef = useRef(500); // Minimum 500ms for processing indicator
    const currentTurnAssistantMessageIdsRef = useRef([]);
    const currentTurnThinkingPhasesRef = useRef([]);
    const currentTurnReasoningTokensRef = useRef(0);
    const currentTurnReasoningTextRef = useRef('');
    const currentTurnStartedAtRef = useRef(null);
    const currentTurnToolHistoryRef = useRef([]);
    const currentTurnLastMeaningfulResultRef = useRef(null);
    const currentTurnFinalizedRef = useRef(false);
    // Multi-agent turn tracking: the ordered agent plan, the cursor into it, and
    // each intermediate agent's streamed answer (collapsed under the final reply).
    const currentTurnAgentPlanRef = useRef([]);
    const currentTurnAgentIdxRef = useRef(0);
    const currentTurnAgentStepsRef = useRef([]);
    const [thinkingPhase, setThinkingPhase] = useState('');
    // Tracks messages the user has manually COLLAPSED. The thinking timeline is
    // EXPANDED by default so the full COT is visible after each turn; clicking
    // the summary collapses it. (Inverted from the old "expanded" set, which
    // started collapsed and only auto-opened for Tool:/Running phases.)
    const [expandedThinking, setExpandedThinking] = useState(new Set());
    const [copiedId, setCopiedId] = useState(null);
    // Live "now" tick: re-renders ~every 150ms while a turn is in flight so the
    // live-thinking duration counter advances and the phase-chip rail (which
    // reads from a ref) stays reactive. Idle when nothing is streaming/processing.
    const [nowTick, setNowTick] = useState(0);
    const [attachments, setAttachments] = useState([]);
    // Starter-prompt chips: fetched from the backend on mount (fallback to DEFAULT_SUGGESTED_PROMPTS).
    const [suggestedPrompts, setSuggestedPrompts] = useState(DEFAULT_SUGGESTED_PROMPTS);
    // Visible chips: a random subset of 4 from the backend list. Reshuffled each time the
    // welcome screen (re)appears (on load AND after the user clears the chat) so the chips
    // actually vary across sessions — not just once per page load.
    const [visiblePrompts, setVisiblePrompts] = useState([]);
    const isWelcome = CheckIsEmptyArray(chatMsg);
    useEffect(() => {
        if (!isWelcome) return;  // only (re)shuffle when the welcome screen is showing
        const a = [...suggestedPrompts];
        for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [a[i], a[j]] = [a[j], a[i]];
        }
        setVisiblePrompts(a.slice(0, 4));
    }, [isWelcome, suggestedPrompts]);
    const fileInputRef = useRef(null);
    // Confirmation gate state. toolStreamTimeoutRef hoists sendToolResultToServer's
    // 300s watchdog so the gate can disarm it before awaiting a human decision.
    const toolStreamTimeoutRef = useRef(null);
    const pendingConfirmationRef = useRef(null);
    const preparedFormChangesRef = useRef([]);
    const [pendingConfirmation, setPendingConfirmation] = useState(null);
    const [confirmationEdit, setConfirmationEdit] = useState(null);

    // Set default mode to docker on component mount
    useEffect(() => {
        if (!localStorage.getItem('chatbot_mode')) {
            localStorage.setItem('chatbot_mode', 'docker');
        }
    }, []);

    useEffect(() => {
        init();
    }, []);

    // Fetch backend-driven starter prompts (served by the langgraph image at
    // ${LANGGRAPH_URL}/suggestions). Plain GET, NO auth header -> stays a CORS
    // "simple request" (no preflight). The backend is the single source of truth:
    // an empty list (CHATBOT_SUGGESTIONS_ENABLED=false) hides ALL chips, so we apply
    // the response even when it is empty. Only a failed/non-OK request leaves the
    // (also empty) default in place.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await fetch(`${LANGGRAPH_URL}/suggestions`, { method: 'GET' });
                if (!resp.ok) return;
                const data = await resp.json();
                const items = Array.isArray(data && data.suggestions) ? data.suggestions : [];
                if (!cancelled) {
                    setSuggestedPrompts(items.map(s => ({ preview: s.preview, prompt: s.prompt })));
                }
            } catch (_e) {
                // Backend unreachable / non-JSON -> keep the (empty) default.
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        //console.log('useEffect', chatMsg, showProcessing, showTyping)
        if (didMount) {
            scrollToBottom();
        }
    }, [chatMsg, showProcessing, showTyping]);

    // Copy handler: reformat selected chat text with "user:" / "chatbot:" prefixes
    useEffect(() => {
        const container = chatMsgRef.current;
        if (!container) return;

        const handleCopy = (e) => {
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed) return;

            const range = selection.getRangeAt(0);
            if (!container.contains(range.commonAncestorContainer)) return;

            // Collect selected text grouped by message
            const messages = container.querySelectorAll('.message');
            const parts = [];

            messages.forEach(msg => {
                const isUser = msg.classList.contains('user-message');
                const textEl = msg.querySelector('.message-text');
                if (!textEl) return;

                // Check if this message intersects with the selection
                try {
                    const msgRange = document.createRange();
                    msgRange.selectNodeContents(textEl);
                    const overlap = range.compareBoundaryPoints(Range.END_TO_START, msgRange) <= 0 &&
                                   range.compareBoundaryPoints(Range.START_TO_END, msgRange) >= 0;
                    if (!overlap) return;

                    // Get the selected portion of this message
                    const selectedText = textEl.textContent.trim();
                    if (!selectedText) return;

                    const prefix = isUser ? 'user: ' : 'chatbot: ';
                    parts.push(prefix + selectedText);
                } catch (err) {
                    // skip this message
                }
            });

            if (parts.length > 0) {
                e.preventDefault();
                e.clipboardData.setData('text/plain', parts.join('\n\n'));
            }
        };

        container.addEventListener('copy', handleCopy);
        return () => container.removeEventListener('copy', handleCopy);
    }, [chatMsg]);

    useEffect(() => {
        const panel = document.getElementById('sourcePanel');
        const chatWin = document.getElementById('chatWindow');
        if (!panel || !chatWin) return;

        if (showSourcePanel) {
            const chatRect = chatWin.getBoundingClientRect();
            const gap = 16;
            // Always dock the sources panel to the LEFT of the chat window.
            // The old logic fell back to the RIGHT whenever the space left of
            // the chatbox was narrower than the panel (panelWidth + gap). On a
            // typical laptop the chatbox sits well inside that threshold, so the
            // panel almost always ended up on the wrong side. Clamp the left
            // offset to the viewport so the panel never overflows off-screen on
            // narrow windows (it has max-width: 90vw, so it shrinks to fit).
            const panelWidth = panel.offsetWidth || 750;
            let left = chatRect.left - panelWidth - gap;
            if (left < gap) left = gap;
            panel.style.left = left + 'px';
            panel.style.top = chatRect.top + 'px';
            panel.style.height = chatRect.height + 'px';
        }
    }, [showSourcePanel]);

    // Run the live clock only while a turn is in flight (processing OR a message
    // is streaming). anyStreaming flips once per turn, so the interval is set up
    // once when streaming starts and cleared once when it ends — token-by-token
    // chatMsg updates do NOT churn it.
    const anyStreaming = chatMsg.some(m => m.streaming);
    useEffect(() => {
        if (!showProcessing && !anyStreaming) return undefined;
        const id = setInterval(() => setNowTick(t => (t + 1) % 1000000000), 150);
        return () => clearInterval(id);
    }, [showProcessing, anyStreaming]);

    const endDrag = () => {
        if (isDragging) {
            //set temp chat window display css to set final drag position
            chatWindow.style.display = 'flex';

            const chatBubblePosition = {
                left: Math.min(maxX, chatBubble.offsetLeft + offset.left),
                top: Math.min(maxY, chatBubble.offsetTop + offset.top)
            };
            const chatWindowPosition = {
                left: chatBubblePosition.left - chatWindow.offsetWidth + 60,
                top: chatBubblePosition.top - chatWindow.offsetHeight - 10
            };

            chatBubble.style.left = `${chatBubblePosition.left}px`;
            chatBubble.style.top = `${chatBubblePosition.top}px`;
            chatBubble.style.right = 'auto';
            chatBubble.style.bottom = 'auto';

            chatWindow.style.left = `${chatWindowPosition.left}px`;
            chatWindow.style.top = `${chatWindowPosition.top}px`;
            chatWindow.style.right = 'auto';
            chatWindow.style.bottom = 'auto';
            //reset temp chat window display css 
            chatWindow.style.display = null;
        }
        else {
            toggleChat();
        }

        isDragging = false;

        chatBubble.style.transform = 'none';
        chatBubble.style.willChange = '';

        chatWindow.style.transform = 'none';
        chatWindow.style.willChange = '';
        
        //setTimeout(() => {
        //    if (!isDragging) {
        //        toggleChat();
        //    }
        //    isDragging = false;
        //}, 300);

        document.removeEventListener('mousemove', drag, true);
        document.removeEventListener('mouseup', endDrag, true);
    };

    const drag = (e) => {
        isDragging = true;
        const headerHeight = 65;

        if (e.clientX < maxX) {
            offset.left = Math.round(offset.left + e.clientX - last_mouse.x);
        }
        if (e.clientY < maxY && e.clientY > headerHeight) {
            offset.top = Math.round(offset.top + e.clientY - last_mouse.y);
        }
        
        chatBubble.style.transform = `translate(${offset.left}px, ${offset.top}px)`;

        // Move chat window to follow bubble
        if (isOpen) {
            chatWindow.style.transform = `translate(${offset.left + 16}px, ${offset.top + 16}px)`;
        }

        last_mouse.x = e.clientX;
        last_mouse.y = e.clientY;
    };

    const startDrag = (e) => {
        // Check if it's a left click
        if (e.button !== 0) return;

        isDragging = false;

        maxX = window.innerWidth - chatBubble.offsetWidth;
        maxY = window.innerHeight - chatBubble.offsetHeight;

        offset.left = 0;
        offset.top = 0;
        
        last_mouse.x = e.clientX;
        last_mouse.y = e.clientY;

        chatBubble.style.willChange = 'transform';

        if (isOpen) {
            chatWindow.style.willChange = 'transform';
        }

        document.addEventListener('mousemove', drag, true);
        document.addEventListener('mouseup', endDrag, true);

        e.preventDefault();
    };

    const init = () => {
        // DOM elements
        chatBubble = document.getElementById('chatBubble');
        chatWindow = document.getElementById('chatWindow');
        closeChat = document.getElementById('closeChat');
        chatForm = document.getElementById('chatForm');

        // Use refs for elements that may not be available on first render
        chatMessages = chatMsgRef.current;

        sendButton = chatForm.querySelector('button[type="submit"]');
        statusContainer = document.querySelector('.status');
        statusTextEl = statusContainer ? statusContainer.querySelector('.status-text') : null;
        
        // Event Listeners
        chatBubble.addEventListener('mousedown', startDrag, true);

        closeChat.addEventListener('click', () => {
            if (isOpen) {
                toggleChat();
            }
        });

        // Minimize button event listener
        const minimizeChatBtn = document.getElementById('minimizeChat');
        if (minimizeChatBtn) {
            minimizeChatBtn.addEventListener('click', () => {
                if (isOpen) {
                    minimizeChat();
                }
            });
        }

        document.addEventListener('mouseup', () => {
            if (isResizing) {
                isResizing = false;
                resizeDirection = '';
            }
        });

        // NOTE: No blanket click listener on chatWindow.
        // Previously e.preventDefault() was used here to "prevent closing", but it
        // also cancelled the file-input's programmatic .click() — the file dialog is
        // the default action of a file-input click, so preventDefault() suppressed it.
        // The chat window is only closed by the close/minimize buttons and the chat
        // bubble's mouseup handler, so a blanket click handler is not needed.

        document.querySelectorAll('.resize-handle').forEach(handle => {
            handle.addEventListener('mousedown', (e) => {
                if (!isOpen) return; // Only allow resize when chat is open

                // Defensive (M6): never start a resize when the pointer actually
                // landed on the messages area or its native scrollbar. The handles
                // are siblings (not descendants) of .chat-messages, so legitimate
                // handle drags (e.target = the handle div) are unaffected; a
                // mousedown on the scrollbar resolves e.target to .chat-messages.
                if (e.target && e.target.closest && e.target.closest('.chat-messages')) {
                    return;
                }

                isResizing = true;
                resizeDirection = Array.from(handle.classList)
                    .find(c => c.startsWith('resize-') && c !== 'resize-handle');

                const rect = chatWindow.getBoundingClientRect();
                startX = e.clientX;
                startY = e.clientY;
                startWidth = rect.width;
                startHeight = rect.height;
                startLeft = rect.left;
                startBottom = window.innerHeight - rect.bottom;

                // Add visual feedback - use a temporary overlay instead of disabling pointerEvents
                const overlay = document.createElement('div');
                overlay.id = 'resizeOverlay';
                overlay.style.cssText = `
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: 998;
                    cursor: ${handle.style.cursor || 'default'};
                `;
                document.body.appendChild(overlay);

                // Clean up overlay on mouse up
                const removeOverlay = () => {
                    const existingOverlay = document.getElementById('resizeOverlay');
                    if (existingOverlay) {
                        existingOverlay.remove();
                    }
                };

                const originalMouseUp = () => {
                    removeOverlay();
                    document.removeEventListener('mouseup', originalMouseUp);
                };

                document.addEventListener('mouseup', originalMouseUp);

                e.preventDefault();
                e.stopPropagation();
            });
        });

        document.addEventListener('mousemove', (e) => {
            if (!isResizing) return;

            const deltaX = e.clientX - startX;
            const deltaY = startY - e.clientY; // Inverted for bottom-anchored

            let newWidth = startWidth;
            let newHeight = startHeight;
            let newLeft = startLeft;
            let newBottom = startBottom;

            // Handle different resize directions
            if (resizeDirection.includes('right')) {
                newWidth = startWidth + deltaX;
            }
            if (resizeDirection.includes('left')) {
                newWidth = startWidth - deltaX;
                newLeft = startLeft + deltaX;
            }
            if (resizeDirection.includes('top')) {
                newHeight = startHeight + deltaY;
            }
            if (resizeDirection.includes('bottom')) {
                newHeight = startHeight - deltaY;
                newBottom = startBottom + deltaY;
            }

            // Apply constraints
            const minWidth = 300;
            const minHeight = 400;
            const maxWidth = window.innerWidth * 0.9;
            const maxHeight = window.innerHeight * 0.9;

            newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
            newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

            // Ensure position stays within viewport
            newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - newWidth));
            newBottom = Math.max(0, newBottom);

            chatWindow.style.width = `${newWidth}px`;
            chatWindow.style.height = `${newHeight}px`;

            // Only apply position changes for left/top resizing
            if (resizeDirection.includes('left')) {
                chatWindow.style.right = `${window.innerWidth - newLeft - newWidth}px`;
                chatWindow.style.left = 'auto';
            }
            if (resizeDirection.includes('top')) {
                chatWindow.style.bottom = `${newBottom}px`;
                chatWindow.style.top = 'auto';
            }

            e.preventDefault();
        });

        // Initialize
        initializeChat({ silent: true })
            .then((id) => {
                if (!id) {
                    console.warn('Initial chat warm-up did not establish a thread. The assistant will retry when opened.');
                }
            });

        setDidMount(true);
    };
    
    const setChatInputEnabled = (enabled, placeholder = defaultInputPlaceholder) => {
        setDisableInput(!enabled);

        if (sendButton) {
            sendButton.disabled = !enabled;
            sendButton.classList.toggle('disabled', !enabled);
        }
    };

    const updateConnectionStatus = (state, message) => {
        if (!statusContainer) {
            return;
        }
        statusContainer.dataset.state = state;
        const copy = message || CONNECTION_STATUS_COPY[state] || state;
        if (statusTextEl) {
            statusTextEl.textContent = copy;
        } else {
            statusContainer.textContent = copy;
        }
    };

    const fetchWithTimeout = (url, options = {}, timeout = 15000) => {
        const controller = new AbortController();
        const fetchOptions = { ...options, signal: controller.signal };
        const timeoutId = setTimeout(() => {
            try {
                return controller.abort('fetchWithTimeout');
            }
            catch (err) {
                console.error('fetchWithTimeout setTimeout error', err);
            }
        }, timeout);
        return fetch(url, fetchOptions).finally(() => clearTimeout(timeoutId));
    };

    const getAuthHeaders = () => {
        const headers = {
            'Content-Type': 'application/json'
        };

        // Use the ASP.NET id_token directly as the Bearer token
        const authToken = localStorage.getItem('id_token');

        if (authToken) {
            headers.Authorization = `Bearer ${authToken}`;
        } else {
            console.warn('No id_token in localStorage - request will be unauthenticated');
        }

        // Add x-functions-key for production mode
        if (!CONFIG.isDevelopment && CONFIG.FUNCTIONS_KEY) {
            headers['x-functions-key'] = CONFIG.FUNCTIONS_KEY;
        }

        return headers;
    };

    // Utility Functions
    const getCurrentTime = () => {
        return new Date().toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const scrollToBottom = () => {
        const { current: chatMsgRefCurrent } = chatMsgRef;
        const { current: inputRefCurrent } = inputRef;
        
        chatMsgRefCurrent && chatMsgRefCurrent.scrollTo({ top: chatMsgRefCurrent.scrollHeight });
        inputRefCurrent && inputRefCurrent.focus();
    };
    
    const addMessage = (content, isUser = false, messageId = null, citations = null, thinking = null, msgAttachments = null, streaming = false) => {
        let newMsg = [...chatMsg];

        let pushObj = {
            content,
            isUser,
            messageId,
            citations,
            streaming
        };
        if (msgAttachments && msgAttachments.length > 0) {
            pushObj.attachments = msgAttachments;
        }
        newMsg.push(pushObj);

        setChatMsg((prevChatMsg) => {
            if (!isUser) {
                const lastAssistantMessage = [...prevChatMsg].reverse().find((message) => !message.isUser);
                if (lastAssistantMessage && lastAssistantMessage.content === content) {
                    return prevChatMsg;
                }
            }

            let newMsg = [...prevChatMsg];
            let newObj = {
                content,
                isUser,
                messageId,
                streaming
            };

            // Add citations if provided
            if (citations && citations.length > 0) {
                newObj.citations = citations;
            }

            // Add thinking info if provided
            if (thinking) {
                newObj.thinking = thinking;
            }

            // Add attachments if provided
            if (msgAttachments && msgAttachments.length > 0) {
                newObj.attachments = msgAttachments;
            }

            newMsg.push(newObj);

            return newMsg;
        });
    };

    const updateMessage = (messageId, content, citations = null, thinking = null, streaming = null) => {
        setChatMsg((prevChatMsg) => {
            let newMsg = [...prevChatMsg]
            let idx = prevChatMsg.findLastIndex(m => {
                return m.messageId === messageId && !m.isUser;
            });

            if (idx > -1) {
                newMsg[idx].content = content;
                // Only update citations if explicitly provided (not null/undefined)
                // This preserves existing citations during streaming updates
                if (citations !== null && citations !== undefined) {
                    newMsg[idx].citations = citations;
                }
                // If citations parameter is explicitly provided but empty, clear them
                else if (citations === null && prevChatMsg[idx].citations) {
                    // Keep existing citations if not explicitly clearing them
                    // Don't do anything - preserve the existing citations
                }
                // Update thinking info if provided
                if (thinking) {
                    newMsg[idx].thinking = thinking;
                }
                // Streaming flag: while true the message renders as a live
                // "Thinking…" panel; finalized answers set it to false.
                if (streaming !== null && streaming !== undefined) {
                    newMsg[idx].streaming = streaming;
                }
            }
            else {
                const lastAssistantMessage = [...prevChatMsg].reverse().find((message) => !message.isUser);
                if (lastAssistantMessage && lastAssistantMessage.content === content) {
                    return prevChatMsg;
                }

                let newObj = {
                    content,
                    isUser: false,
                    messageId
                };
                if (citations && citations.length > 0) {
                    newObj.citations = citations;
                }
                if (thinking) {
                    newObj.thinking = thinking;
                }
                newMsg.push(newObj);
            }

            return newMsg;
        });
    };

    const trackAssistantMessage = (messageId) => {
        if (!messageId) {
            return;
        }

        if (!currentTurnAssistantMessageIdsRef.current.includes(messageId)) {
            currentTurnAssistantMessageIdsRef.current = [
                ...currentTurnAssistantMessageIdsRef.current,
                messageId
            ];
        }
    };

    const clearCurrentTurnAssistantMessages = () => {
        const messageIds = currentTurnAssistantMessageIdsRef.current;
        if (!messageIds.length) {
            return;
        }

        setChatMsg((prevChatMsg) => prevChatMsg.filter(
            message => message.isUser || !messageIds.includes(message.messageId)
        ));
        currentTurnAssistantMessageIdsRef.current = [];
    };

    const beginCurrentTurnThinking = () => {
        currentTurnThinkingPhasesRef.current = [];
        currentTurnReasoningTokensRef.current = 0;
        currentTurnReasoningTextRef.current = '';
        currentTurnStartedAtRef.current = Date.now();
        currentTurnToolHistoryRef.current = [];
        currentTurnLastMeaningfulResultRef.current = null;
        currentTurnFinalizedRef.current = false;
        currentTurnAgentPlanRef.current = [];
        currentTurnAgentIdxRef.current = 0;
        currentTurnAgentStepsRef.current = [];
    };

    const addCurrentTurnPhase = (phase) => {
        if (!phase) {
            return;
        }

        const phases = currentTurnThinkingPhasesRef.current;
        if (phases[phases.length - 1] !== phase) {
            currentTurnThinkingPhasesRef.current = [...phases, phase];
        }
    };

    // ---- Multi-agent (rag->bms->da) intermediate-output routing ----
    // Per-domain labels for the multi-agent action trace — surfaces the agentic
    // architecture (a roster of specialized agents) instead of a generic "Sub-agent".
    const AGENT_LABELS = {
        rag: 'Knowledge Retrieval Agent',
        bms: 'BMS Operations Agent',
        da: 'Analytics Agent',
        p44: 'Schedule & Vessel Agent',
        dnd: 'D&D Tariff Agent',
    };
    // True when the agent currently streaming is the LAST in the plan (its output is
    // the reply). Single-intent turns (no plan) are always final -> today's behavior.
    const isFinalAgentStreaming = () => {
        const plan = currentTurnAgentPlanRef.current;
        if (plan.length < 2) return true;
        return currentTurnAgentIdxRef.current >= plan.length - 1;
    };
    const currentAgentLabel = () => {
        const plan = currentTurnAgentPlanRef.current;
        const key = plan[currentTurnAgentIdxRef.current];
        return AGENT_LABELS[key] || 'Specialist Agent';
    };
    // Accumulate an intermediate agent's streamed answer as a trace entry (replaces
    // the previous chunk for the same label so the final text is the full answer).
    const addCurrentTurnAgentStep = (label, text) => {
        const t = (text || '').trim();
        if (!t) return;
        const steps = currentTurnAgentStepsRef.current;
        const ex = steps.find((s) => s.label === label);
        if (ex) ex.text = t;
        else steps.push({ label, text: t });
        currentTurnAgentStepsRef.current = [...steps];
    };

    const addCurrentTurnReasoningTokens = (count) => {
        if (typeof count === 'number' && Number.isFinite(count) && count > 0) {
            currentTurnReasoningTokensRef.current += count;
        }
    };

    // Capture the model's real reasoning/thinking text when the deployment
    // streams it (Kimi reasoning_content). Surfaced in the "Analyzing your
    // question" phase detail instead of a canned description. No-op otherwise.
    const addCurrentTurnReasoningText = (text) => {
        const t = (text || '').trim();
        if (t && currentTurnReasoningTextRef.current.indexOf(t) === -1) {
            currentTurnReasoningTextRef.current = currentTurnReasoningTextRef.current
                ? `${currentTurnReasoningTextRef.current}\n${t}`
                : t;
        }
    };

    const getCurrentTurnThinkingInfo = () => {
        const phases = currentTurnThinkingPhasesRef.current;
        const reasoningTokens = currentTurnReasoningTokensRef.current;
        const startedAt = currentTurnStartedAtRef.current || processingStartTimeRef.current;

        // Surface the full action chain (already tracked for loop-guarding) so the
        // expandable thinking section can show each tool's name + params + result.
        // `ok` is a heuristic: getToolResultText prefixes real errors with
        // "Error executing ..."/"ERROR:" and tool not-found cases report "not found".
        const tools = currentTurnToolHistoryRef.current.map((entry) => {
            const output = entry.output_text || '';
            return {
                tool_name: entry.tool_name,
                params: entry.params || {},
                output_text: output,
                ok: !/^error\b/i.test(output) && !/\bnot found\b/i.test(output)
            };
        });

        const agentSteps = currentTurnAgentStepsRef.current;
        if (!phases.length && reasoningTokens <= 0 && tools.length === 0 && agentSteps.length === 0) {
            return null;
        }

        return {
            phases,
            reasoningTokens,
            reasoningText: currentTurnReasoningTextRef.current,
            duration: startedAt ? Date.now() - startedAt : 0,
            tools,
            agentSteps
        };
    };

    const stripToolPrefix = (text) => (text || '')
        .replace(/^[a-z_]+:\s*/i, '')        // drop 'read_so_tab: '
        .replace(/^Tab:\s*[^\n]*\n?/i, '')   // drop 'Tab: Booking Approval\n'
        .trim();

    const recordToolResult = (toolName, params, result) => {
        const outputText = getToolResultText(toolName, result);
        const entry = { tool_name: toolName, params, output_text: outputText };

        currentTurnToolHistoryRef.current = [
            ...currentTurnToolHistoryRef.current,
            entry
        ];

        if (
            toolName === 'read_so_tab'
            || toolName === 'read_so_header'
            || toolName === 'read_current_page'
        ) {
            currentTurnLastMeaningfulResultRef.current = entry;
        }
    };

    const buildLoopGuardReply = () => {
        const lastMeaningful = currentTurnLastMeaningfulResultRef.current;
        if (!lastMeaningful) {
            return null;
        }

        const content = stripToolPrefix(lastMeaningful.output_text);
        if (!content) {
            return null;
        }

        if (content.includes('/AccessDeclined') || content.includes('403 Forbidden')) {
            return "I reached the requested booking page, but the system returned 403 Forbidden, so your account does not currently have permission to view that record.";
        }

        // The loop guard is a safety backstop. When it fires after the user asked
        // for an ACTION (approve/save/edit), the action was NOT completed this turn.
        // Say so plainly and invite a retry — never present raw DOM text as a
        // normal answer, and never claim the action succeeded. (M2/M4/M5)
        const tabOrPage = lastMeaningful.params && (lastMeaningful.params.tab_name || lastMeaningful.params.tab)
            ? (lastMeaningful.params.tab_name || lastMeaningful.params.tab)
            : 'the current page';
        const guardNote = `[Action not completed] I read ${tabOrPage} but did not finish the requested action in this turn. If you asked me to approve, change, or submit something, please re-send the request and I will perform it.\n\n`;
        const fenced = content.split('\n').length > 1 || content.length > 120
            ? `\`\`\`\n${content}\n\`\`\``
            : content;
        return `${guardNote}${fenced}`;
    };

    const shouldFinalizeToolLoop = (nextToolCalls) => {
        const lastMeaningful = currentTurnLastMeaningfulResultRef.current;
        const history = currentTurnToolHistoryRef.current;

        // 403 / access-declined short-circuit (unchanged behavior).
        if (lastMeaningful?.tool_name === 'read_current_page') {
            const output = lastMeaningful.output_text || '';
            if (output.includes('/AccessDeclined') || output.includes('403 Forbidden')) {
                return true;
            }
        }

        // Only cut the loop on a TRUE redundant read: the LLM is about to call
        // read_so_tab for a tab it ALREADY read this turn, with NO intervening
        // action tool. Previously the guard fired on ANY next call that wasn't
        // open_shipping_order, which discarded click_button('Approve') and other
        // actions after a read and substituted a canned "I already retrieved the
        // requested tab data..." reply (the exact M4 symptom). Everything else —
        // click_button, open_so_for_edit, handle_popup_confirm, navigate, paginate,
        // another read_so_header, etc. — must be allowed to run. (M2/M4)
        const ACTION_TOOL_NAMES = new Set([
            'click_button', 'click_list_item', 'open_so_for_edit', 'open_shipping_order',
            'switch_so_tab', 'navigate_to_page', 'search_booking', 'paginate',
            'apply_filters', 'apply_custom_filter', 'handle_popup_confirm',
            'dismiss_all_popups', 'batch_form_actions', 'fill_form', 'read_form',
            'fill_table_cell', 'select_option', 'set_date', 'toggle_checkbox', 'select_radio'
        ]);
        const lastMeaningfulIdx = history.findIndex((h) => h === lastMeaningful);
        const anyActionSinceLastRead = history
            .slice(lastMeaningfulIdx + 1)
            .some((h) => ACTION_TOOL_NAMES.has(h.tool_name));

        if (
            !anyActionSinceLastRead
            && lastMeaningful?.tool_name === 'read_so_tab'
            && nextToolCalls.some((c) => {
                const incomingTab = c.params && (c.params.tab_name || c.params.tab);
                const lastTab = lastMeaningful.params && (lastMeaningful.params.tab_name || lastMeaningful.params.tab);
                return c.tool_name === 'read_so_tab'
                    && (!incomingTab || !lastTab || String(incomingTab).toLowerCase() === String(lastTab).toLowerCase());
            })
        ) {
            return true;
        }

        // Absolute safety cap for runaway loops (unchanged).
        return history.length >= 20;
    };

    const finalizeToolLoop = () => {
        if (currentTurnFinalizedRef.current) {
            return true;
        }

        const fallbackReply = buildLoopGuardReply();
        if (!fallbackReply && currentTurnAssistantMessageIdsRef.current.length === 0) {
            return false;
        }

        currentTurnFinalizedRef.current = true;

        const thinkingInfo = getCurrentTurnThinkingInfo();
        const existingMessageIds = currentTurnAssistantMessageIdsRef.current;

        if (existingMessageIds.length > 0) {
            const latestMessageId = existingMessageIds[existingMessageIds.length - 1];
            if (thinkingInfo) {
                const latestMessage = chatMsg.find((message) => message.messageId === latestMessageId);
                if (latestMessage) {
                    updateMessage(latestMessageId, latestMessage.content, latestMessage.citations, thinkingInfo);
                }
            }
            setDisableInput(false);
            hideProcessingIndicator();
            return true;
        }

        const finalMessageId = `tool-loop-final-${Date.now()}`;
        addMessage(fallbackReply, false, finalMessageId, null, thinkingInfo);
        trackAssistantMessage(finalMessageId);
        setDisableInput(false);
        hideProcessingIndicator();
        return true;
    };

    const showTypingIndicator = () => {
        setShowTyping(true);
    };

    const showProcessingIndicator = () => {
        processingStartTimeRef.current = Date.now();
        setShowProcessing(true);
        setThinkingPhase('Analyzing your question');
    };

    const hideProcessingIndicator = () => {
        // Ensure processing indicator shows for at least 500ms
        const elapsed = processingStartTimeRef.current ? Date.now() - processingStartTimeRef.current : 0;
        const minDelay = minProcessingDelayRef.current;

        if (elapsed < minDelay) {
            // Hide it after the minimum delay
            setTimeout(() => {
                setShowProcessing(false);
                setThinkingPhase('');
            }, minDelay - elapsed);
        } else {
            // Already shown for enough time, hide immediately
            setShowProcessing(false);
            setThinkingPhase('');
        }
    };

    const hideTypingIndicator = () => {
        setShowTyping(false);
    };

    const toggleThinking = (messageId) => {
        setExpandedThinking(prev => {
            const next = new Set(prev);
            if (next.has(messageId)) {
                next.delete(messageId);
            } else {
                next.add(messageId);
            }
            return next;
        });
    };

    // Copy the rendered text of a bot bubble (what the user sees, not raw
    // markdown) to the clipboard, with a brief "copied" check-mark feedback.
    const copyMessage = async (e, messageId) => {
        e.stopPropagation();
        try {
            const bubble = e.currentTarget.closest('.message-text');
            const text = bubble ? bubble.innerText : '';
            await navigator.clipboard.writeText(text);
            setCopiedId(messageId);
            setTimeout(() => setCopiedId((id) => (id === messageId ? null : id)), 1500);
        } catch (err) {
            console.warn('[Chatbot] copy failed:', err);
        }
    };

    // Resolve the in-flight confirmation card. Single-flight: a late second click
    // or a late stream chunk is a no-op once pendingConfirmationRef is null.
    const resolveConfirmation = useCallback((choice, edits) => {
        const p = pendingConfirmationRef.current;
        if (!p) return;
        pendingConfirmationRef.current = null;
        setPendingConfirmation(null);
        setConfirmationEdit(null);
        setChatMsg(prev => prev.map(m => m.messageId === p.cardMessageId ? { ...m, resolvedChoice: choice } : m));
        setTimeout(() => setChatMsg(prev => prev.filter(m => m.messageId !== p.cardMessageId)), 1500);
        p.resolve({ choice, edits: edits || [] });
    }, []);
    const startKeepEdit = useCallback((cardMessageId, confirmation) => {
        const items = (confirmation.prepared || []).map(x => ({ ...x, editValue: x.value }));
        setConfirmationEdit({ cardMessageId: cardMessageId, items: items });
    }, []);
    const rejectAllPendingConfirmations = useCallback(() => {
        const p = pendingConfirmationRef.current;
        if (!p) return;
        pendingConfirmationRef.current = null;
        setPendingConfirmation(null);
        setConfirmationEdit(null);
        setChatMsg(prev => prev.filter(m => m.messageId !== p.cardMessageId));
        p.reject(new Error('CLEARED'));
    }, []);

    const showError = (message) => {
        setErrorMsg(message);

        setTimeout(() => {
            setErrorMsg('');
        }, 5000);
        
    };

    // LangGraph API Functions
    const createThread = async () => {
        try {
            const response = await fetchWithTimeout(`${LANGGRAPH_URL}/threads`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    metadata: { created_at: new Date().toISOString() }
                }),
                mode: 'cors'
            });

            if (!response.ok) {
                throw new Error(`Failed to create thread: ${response.statusText}`);
            }

            const data = await response.json();

            // Store thread ID in sessionStorage for persistence across page refreshes
            sessionStorage.setItem('langgraph_thread_id', data.thread_id);
            //console.log('Thread created:', data.thread_id);

            // Clear conversation history when creating a new thread
            conversationHistory = [];
            console.log('Conversation history cleared for new thread');

            return data.thread_id;
        } catch (error) {
            console.error('Error creating thread:', error);
            throw error;
        }
    };

    const verifyThreadExists = async (id) => {
        const response = await fetchWithTimeout(`${LANGGRAPH_URL}/threads/${id}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            mode: 'cors'
        });
        if (response.ok) {
            return true;
        }
        if (response.status === 404) {
            return false;
        }
        throw new Error(`Unexpected status verifying thread: ${response.status}`);
    };

    // >>> LANGGRAPH CHATBOT INTEGRATION — Kimi Tool Call Parser >>>
    /**
     * Parse Kimi-K2.5's raw tool call tokens from streaming content.
     * Kimi outputs: <|tool_call_begin|>functions.NAME:ID<|tool_call_argument_begin|>JSON<|tool_call_end|>
     * Converts to: __TOOL_CALL__:{"tool_call": {"tool_name": "...", "params": {...}}}
     */
    function parseKimiToolCall(content) {
        const match = content.match(/<\|tool_call_begin\|>functions\.([^:]+):\d+<\|tool_call_argument_begin\|>(.*?)<\|tool_call_end\|>/);
        if (!match) return null;

        let toolName = match[1];

        try {
            const params = JSON.parse(match[2]);
            // Map parameter names from prompt conventions to actual tool params
            const paramMapping = { page_key: 'page' };
            const mappedParams = {};
            for (const [k, v] of Object.entries(params)) {
                mappedParams[paramMapping[k] || k] = v;
            }
            return `__TOOL_CALL__:${JSON.stringify({
                tool_call: { tool_name: toolName, params: mappedParams }
            })}`;
        } catch (e) {
            console.error('[Chatbot] Failed to parse Kimi tool args:', e);
            return null;
        }
    }

        /**
         * Extract embedded tool instructions from assistant content.
         * Supports JSON markers and XML-style <function_calls> blocks.
         */
        function extractToolInstructionsFromContent(content) {
            if (typeof content !== 'string' || !content) {
                return null;
            }

            const xmlStart = content.indexOf('<function_calls>');
            const xmlEnd = content.indexOf('</function_calls>');
            if (xmlStart !== -1 && xmlEnd !== -1) {
                const xmlBlock = content.slice(xmlStart, xmlEnd + '</function_calls>'.length);
                const toolCalls = [];
                const invokePattern = /<invoke\s+name="([^"]+)">([\s\S]*?)<\/invoke>/g;
                let invokeMatch;

                while ((invokeMatch = invokePattern.exec(xmlBlock)) !== null) {
                    const [, toolName, body] = invokeMatch;
                    const params = {};
                    const argumentPattern = /<(parameter|arg)\s+name="([^"]+)">([\s\S]*?)<\/(parameter|arg)>/g;
                    let argumentMatch;

                    while ((argumentMatch = argumentPattern.exec(body)) !== null) {
                        const [, , name, value] = argumentMatch;
                        params[name] = value.trim();
                    }

                    toolCalls.push({ tool_name: toolName, params });
                }

                if (toolCalls.length > 0) {
                    return {
                        toolCalls,
                        leadingText: content.slice(0, xmlStart).trim(),
                        trailingText: content.slice(xmlEnd + '</function_calls>'.length).trim()
                    };
                }
            }

            const prefixes = ['__TOOL_CALL__:', 'TOOL_CALL:', 'TOOL_CALL('];
            const genericMarkerMatches = Array.from(content.matchAll(/__[A-Z_]+__:/g)).map(match => match[0]);
            for (const marker of genericMarkerMatches) {
                if (!prefixes.includes(marker)) {
                    prefixes.push(marker);
                }
            }
            let markerIndex = -1;
            let matchedPrefix = '';

            for (const prefix of prefixes) {
                const index = content.indexOf(prefix);
                if (index !== -1 && (markerIndex === -1 || index < markerIndex)) {
                    markerIndex = index;
                    matchedPrefix = prefix;
                }
            }

            if (markerIndex === -1) {
                return null;
            }

            const jsonStart = markerIndex + matchedPrefix.length;
            const jsonSlice = content.slice(jsonStart).trimStart();
            const firstBraceIndex = jsonSlice.indexOf('{');

            if (firstBraceIndex === -1) {
                return null;
            }

            let depth = 0;
            let inString = false;
            let isEscaped = false;
            let endIndex = -1;

            for (let index = firstBraceIndex; index < jsonSlice.length; index += 1) {
                const ch = jsonSlice[index];

                if (inString) {
                    if (isEscaped) {
                        isEscaped = false;
                    } else if (ch === '\\') {
                        isEscaped = true;
                    } else if (ch === '"') {
                        inString = false;
                    }
                    continue;
                }

                if (ch === '"') {
                    inString = true;
                    continue;
                }

                if (ch === '{') {
                    depth += 1;
                } else if (ch === '}') {
                    depth -= 1;
                    if (depth === 0) {
                        endIndex = index;
                        break;
                    }
                }
            }

            if (endIndex === -1) {
                return null;
            }

            const commandJson = jsonSlice.slice(firstBraceIndex, endIndex + 1);

            try {
                const parsedCommand = JSON.parse(commandJson);
                let command = parsedCommand;

                if (!command.tool_call && command.tool) {
                    command = {
                        tool_call: {
                            tool_name: command.tool,
                            params: command.args || {}
                        }
                    };
                }

                if (!command.tool_call) {
                    return null;
                }

                return {
                    toolCalls: [command.tool_call],
                    commandText: `__TOOL_CALL__:${commandJson}`,
                    leadingText: content.slice(0, markerIndex).trim(),
                    trailingText: jsonSlice.slice(endIndex + 1).trim()
                };
            } catch (e) {
                console.error('[Chatbot] Failed to parse embedded tool call:', e, content);
                return null;
            }
        }

        function extractToolCallFromContent(content) {
            const extracted = extractToolInstructionsFromContent(content);
            if (!extracted || !extracted.toolCalls || extracted.toolCalls.length === 0) {
                return null;
            }

            return {
                command: {
                    tool_call: extracted.toolCalls[0],
                    tool_calls: extracted.toolCalls
                },
                leadingText: extracted.leadingText,
                trailingText: extracted.trailingText,
                commandText: extracted.commandText || ''
            };
        }

        function getToolResultText(toolName, result) {
            if (result?.error) {
                return `Error executing ${toolName}: ${result.error}`;
            }

            const text = result?.result?.content?.[0]?.text;
            if (text) {
                return `${toolName}: ${text}`;
            }

            return `${toolName}: completed successfully`;
        }

        function hasToolCallMarker(content) {
            return typeof content === 'string'
                && (
                    content.includes('__TOOL_CALL')
                    || /__[A-Z_]+__:/i.test(content)
                    || content.includes('TOOL_CALL')
                    || content.includes('<function_calls')
                    || content.includes('<invoke ')
                );
        }

        function looksLikeToolCallPrefix(content) {
            const trimmed = typeof content === 'string' ? content.trim() : '';
            return trimmed.startsWith('__') || trimmed.startsWith('<') || hasToolCallMarker(trimmed);
        }

        /**
         * Strip Kimi/tool-call artifacts from streaming content so raw tokens
         * (e.g. `<|tool_calls_section_begin|>functions.retrieve_chroma_docs:0{...}`)
         * NEVER render to the user as the answer. Anything from the first tool
         * marker onward is dropped; stray partial markers are removed. This is
         * the core fix for the "unreadable chain-of-thought" leak (M2/CoT).
         */
        const TOOL_STREAM_MARKERS = [
            '<|tool_calls_section_begin|>', '<|tool_call_begin|>',
            '<|tool_call_argument_begin|>', '<|tool_call_end|>',
            '<|tool_calls_section_end|>', '__TOOL_CALL__:',
            '<function_calls>', '<invoke ',
        ];
        function sanitizeStreamContent(text) {
            if (!text) return '';
            let t = String(text);
            let cut = t.length;
            for (const mk of TOOL_STREAM_MARKERS) {
                const i = t.indexOf(mk);
                if (i !== -1 && i < cut) cut = i;
            }
            t = t.slice(0, cut);
            // remove any stray partial <|...|> tokens or XML tool tags
            t = t.replace(/<\|[^|]*\|>/g, '').replace(/<\/?(?:function_calls|invoke|parameter|arg)\b[^>]*>/g, '');
            return t.trim();
        }

        function isStreamToolMessage(msg) {
            return msg?.type === 'ai' || msg?.role === 'assistant' || msg?.type === 'tool';
        }

        function getStreamMessageContent(msg) {
            if (!msg) {
                return '';
            }

            if (typeof msg.content === 'string') {
                return msg.content;
            }

            if (Array.isArray(msg.content)) {
                return msg.content
                    .map(c => typeof c === 'string' ? c : c.text || '')
                    .join('');
            }

            return '';
        }

        // Extract reasoning/thinking text the model emitted (Kimi
        // reasoning_content, or LangGraph content blocks of type
        // thinking/reasoning). Returns '' when the deployment streams none —
        // caller then falls back to the canned description.
        function getStreamReasoningContent(msg) {
            if (!msg) return '';
            let out = '';
            if (Array.isArray(msg.content)) {
                out = msg.content
                    .map((c) => {
                        if (!c || typeof c === 'string') return '';
                        if (c.type === 'thinking' || c.type === 'reasoning' || c.type === 'reasoning_text') {
                            return c.thinking || c.reasoning || c.text || '';
                        }
                        return '';
                    })
                    .join('')
                    .trim();
            }
            if (!out) {
                const rc = msg.reasoning_content || msg.reasoning || msg.additional_kwargs?.reasoning_content;
                if (typeof rc === 'string') out = rc.trim();
            }
            return out;
        }
    // <<< LANGGRAPH CHATBOT INTEGRATION — Kimi Tool Call Parser <<<

    // >>> LANGGRAPH CHATBOT INTEGRATION — Tool Call Handler >>>
    /**
     * Handle a __TOOL_CALL__ message from the LangGraph server.
     * Executes the tool and sends the result back.
     * Returns true if handled (don't display as chat message).
     */
    // Capture a form-mutation tool's values into the per-turn prepared-changes ref
    // so the confirmation card can show (and Keep-edit) exactly what the agent set.
    function captureFormMutation(toolName, params) {
        if (!FORM_MUTATION_TOOLS.has(toolName)) return;
        const entries = buildFormChangeEntries(toolName, params);
        if (!entries.length) return;
        const arr = preparedFormChangesRef.current;
        entries.forEach(function (e) {
            const idx = arr.findIndex(function (x) { return x.id === e.id; });
            if (idx >= 0) arr[idx] = e; else arr.push(e);
        });
    }

    // Re-apply the user's edited values to the LIVE form via the primitive tools
    // (these go through bmsTools.executeTool, NOT handleToolCall, so no re-gate).
    async function reapplyFormChanges(editedItems) {
        const applied = [];
        const failed = [];
        for (const it of (editedItems || [])) {
            let prms = null;
            if (it.tool === 'fill_form') prms = { fields: [{ label: it.label, name: it.name, value: it.editValue }] };
            else if (it.tool === 'select_option') prms = { label: it.label, name: it.name, value: it.editValue };
            else if (it.tool === 'set_date') prms = { label: it.label, name: it.name, value: it.editValue };
            else if (it.tool === 'toggle_checkbox') prms = { label: it.label, name: it.name, checked: it.editValue };
            else if (it.tool === 'select_radio') prms = { label: it.label, name: it.name, value: it.editValue };
            else if (it.tool === 'fill_table_cell') prms = { row_identifier: String(it.row_identifier || ''), column_name: it.column_name, value: it.editValue };
            else if (it.tool === 'select_proposed_sailing') prms = { option_number: parseInt(String(it.editValue).replace(/[^0-9]/g, ''), 10) || 1 };
            if (!prms) continue;
            try {
                const r = await executeTool(it.tool, prms);
                const rText = (r && r.success && r.result && r.result.content && r.result.content[0] && r.result.content[0].text) ? r.result.content[0].text : '';
                if (r && r.error) failed.push(it.label + ': ' + r.error);
                else if (/not found|could not|failed|not editable|no proposed/i.test(rText)) failed.push(it.label + ': ' + rText);
                else applied.push(it.label + '=' + it.editValue);
            } catch (err) {
                failed.push(it.label + ': ' + err.message);
            }
        }
        const summary = applied.length ? ('Re-applied: ' + applied.join(', ')) : 'no edits';
        return { summary: summary, failed: failed };
    }

    function failedKeepText(reapply) {
        if (reapply.failed && reapply.failed.length) {
            return 'RESULT: user_kept (re-apply failed: ' + reapply.failed.join('; ') + ') -- the SO remains in edit mode, unsaved, NOTHING committed. Do NOT retry the save; tell the user which field(s) could not be re-applied and ask how to proceed.';
        }
        return 'RESULT: user_kept (' + reapply.summary + '; record left unsaved) -- the SO remains in edit mode, unsaved, NOTHING committed. Tell the user the edits are kept but not saved, and offer to save on request.';
    }

    // Gate a persist tool: show the confirmation card, await the user's decision,
    // then execute (accept), re-apply edits without saving (keep), or abort (reject).
    async function runPersistWithConfirmation(toolName, params) {
        // Disarm the 300s tool-stream watchdog before awaiting a human, else a slow
        // user trips the false 'took too long' fallback under the still-pending card.
        if (toolStreamTimeoutRef.current) { clearTimeout(toolStreamTimeoutRef.current); toolStreamTimeoutRef.current = null; }
        setShowProcessing(false);
        let prepared = preparedFormChangesRef.current.slice();
        if (toolName === 'approve_booking') {
            prepared = prepared.concat([
                { id: 'approve|approval_status', tool: 'select_option', label: 'Approval Status', name: '', value: 'Approved', displayValue: 'Approved', type: 'text' },
                { id: 'approve|sailing_option', tool: 'select_proposed_sailing', label: 'Sailing option', name: '', value: 'option ' + (params.sailing_option || 1), displayValue: 'option ' + (params.sailing_option || 1), type: 'text' }
            ]);
        }
        const cardMessageId = 'confirm-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7);
        setChatMsg(function (prev) {
            return prev.concat([{ messageId: cardMessageId, isUser: false, kind: 'confirmation', content: '', confirmation: { tool_name: toolName, params: params, prepared: prepared } }]);
        });
        setPendingConfirmation({ tool_name: toolName, params: params, prepared: prepared, cardMessageId: cardMessageId });
        const decision = await new Promise(function (resolve, reject) {
            pendingConfirmationRef.current = { tool_name: toolName, params: params, prepared: prepared, resolve: resolve, reject: reject, cardMessageId: cardMessageId };
        });
        if (decision.choice === 'accept') {
            return await executeTool(toolName, params);
        }
        if (decision.choice === 'keep') {
            const reapply = await reapplyFormChanges(decision.edits || []);
            return { success: true, __gateDecision: 'keep', result: { content: [{ type: 'text', text: failedKeepText(reapply) }], tool_results: [{ tool_name: toolName, output_text: 'RESULT: user_kept' }] }, toolName: toolName };
        }
        return { success: true, __gateDecision: 'reject', result: { content: [{ type: 'text', text: rejectTextFor(toolName) }], tool_results: [{ tool_name: toolName, output_text: 'RESULT: user_rejected' }] }, toolName: toolName };
    }

    async function handleToolCall(content) {
        try {
            const extracted = extractToolInstructionsFromContent(content);
            if (!extracted || !extracted.toolCalls || extracted.toolCalls.length === 0) return false;

            const { toolCalls } = extracted;
            console.log('[Chatbot] Executing tool chain:', toolCalls);

            if (currentTurnFinalizedRef.current) {
                return true;
            }

            if (shouldFinalizeToolLoop(toolCalls)) {
                return finalizeToolLoop();
            }

            clearCurrentTurnAssistantMessages();

            // Tool-execution progress is shown in the thinking/COT panel via
            // setThinkingPhase / addCurrentTurnPhase below — NOT as a raw chat message.
            // Disable input during tool execution
            setDisableInput(true);
            setShowProcessing(true);
            setThinkingPhase(toolCalls.length > 1 ? 'Running browser actions' : `Running ${toolCalls[0].tool_name}`);
            addCurrentTurnPhase(toolCalls.length > 1 ? 'Running browser actions' : `Running ${toolCalls[0].tool_name}`);

            const toolResults = [];
            for (let index = 0; index < toolCalls.length; index += 1) {
                const { tool_name, params } = toolCalls[index];
                console.log(`[Chatbot] Executing tool: ${tool_name}`, params);
                setThinkingPhase(`Running ${tool_name}`);
                addCurrentTurnPhase(`Tool: ${tool_name}`);

                let result;
                if (isPersistToolCall(tool_name, params)) {
                    result = await runPersistWithConfirmation(tool_name, params);
                } else {
                    captureFormMutation(tool_name, params);
                    result = await executeTool(tool_name, params);
                }
                recordToolResult(tool_name, params, result);
                toolResults.push({ tool_name, result });
                if (result && (result.__gateDecision === 'reject' || result.__gateDecision === 'keep')) break;
            }

            const lastToolResult = toolResults[toolResults.length - 1];
            const combinedText = toolResults
                .map(({ tool_name, result }) => getToolResultText(tool_name, result))
                .join('\n');

            const combinedResult = {
                success: true,
                result: {
                    content: [{ type: 'text', text: combinedText }],
                    tool_results: toolResults.map(({ tool_name, result }) => ({
                        tool_name,
                        output_text: getToolResultText(tool_name, result)
                    }))
                },
                toolName: lastToolResult.tool_name
            };

            // Send result back to LangGraph
            await sendToolResultToServer(lastToolResult.tool_name, combinedResult);

            // Ensure the chat unlocks after the full tool chain completes.
            setDisableInput(false);

            return true; // Handled — don't display __TOOL_CALL__ as message
        } catch (error) {
            if (error && error.message === 'CLEARED') {
                setDisableInput(false);
                return false;
            }
            console.error('[Chatbot] Error handling tool call:', error);
            setDisableInput(false);
            return false;
        }
    }

    /**
     * Send tool execution result back to the LangGraph server.
     * Uses the same SSE streaming endpoint as sendMessage().
     */
    async function sendToolResultToServer(toolName, result) {
        if (!threadId) {
            console.error('[Chatbot] No thread ID for tool result');
            setDisableInput(false);
            return;
        }

        const payload = {
            assistant_id: ASSISTANT_ID,
            input: {
                messages: [
                    {
                        role: 'user',
                        content: `__TOOL_RESULT__:${JSON.stringify({
                            tool_name: toolName,
                            result: result
                        })}`
                    }
                ]
            },
            stream_mode: ['messages', 'updates']
        };

        try {
            const response = await fetch(
                `${LANGGRAPH_URL}/threads/${threadId}/runs/stream`,
                {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    mode: 'cors',
                    body: JSON.stringify(payload)
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            // Process the streaming response (same SSE pattern as sendMessage)
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let currentEvent = '';
            let thisStreamingContent = '';
            let hasStartedStreaming = false;
            let kimiToolCallContent = null; // >>> LANGGRAPH CHATBOT INTEGRATION — Deferred Kimi tool call
            const thisMessageId = `tool-response-${Date.now()}`;
            let citations = [];
            let lastPhase = '';
            const addThinkingPhase = (phase) => {
                if (phase && phase !== lastPhase) {
                    lastPhase = phase;
                    setThinkingPhase(phase);
                    addCurrentTurnPhase(phase);
                }
            };

            // M3: a tool-result round-trip previously had NO timeout (only the
            // initial sendMessage did), so a hung final hop spun the indicator
            // forever and then produced an empty reply. This watchdog shows a
            // visible message + re-enables input if nothing streams. Multi-hop
            // Kimi turns (navigate -> Search -> read -> answer) can legitimately
            // run 2-3 minutes, so the budget is generous (150s) and only genuine
            // hangs trigger it. The guard (no assistant message shown this turn)
            // makes it a no-op on a slow-but-successful turn and never
            // double-fires across the recursive sendToolResultToServer calls.
            // Clear-before-overwrite: a nested sendToolResultToServer (multi-hop tool turns)
            // must not orphan this call's 300s timer in the shared ref, or the stale timer could
            // fire on a later turn. The nested call inherits watchdog responsibility.
            if (toolStreamTimeoutRef.current) { clearTimeout(toolStreamTimeoutRef.current); }
            toolStreamTimeoutRef.current = setTimeout(() => {
                if (currentTurnAssistantMessageIdsRef.current.length === 0) {
                    console.error('[Chatbot] Tool-result stream timeout (300s, no content)');
                    hideProcessingIndicator();
                    addMessage(
                        'The browser action ran, but the assistant took too long to finish its reply. Would you like me to re-open the Shipping Order and verify the result?',
                        false,
                        `tool-timeout-${Date.now()}`
                    );
                    setDisableInput(false);
                }
            }, 300000);
            const disarmToolStreamTimeout = () => {
                if (toolStreamTimeoutRef.current) { clearTimeout(toolStreamTimeoutRef.current); toolStreamTimeoutRef.current = null; }
            };

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    } else if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.slice(6).trim();
                            if (!jsonStr) continue;
                            const data = JSON.parse(jsonStr);

                            if (currentEvent === 'messages/partial') {
                                const messages = Array.isArray(data) ? data : [data];
                                for (const msg of messages) {
                                    if (isStreamToolMessage(msg)) {
                                        let content = getStreamMessageContent(msg);
                                        if (msg.usage_metadata?.output_token_details?.reasoning) {
                                            addCurrentTurnReasoningTokens(msg.usage_metadata.output_token_details.reasoning);
                                        }
                                        addCurrentTurnReasoningText(getStreamReasoningContent(msg));
                                        if (content && content !== thisStreamingContent) {
                                            if (extractToolInstructionsFromContent(content)) {
                                                setChatMsg((prevChatMsg) => prevChatMsg.filter(m => m.messageId !== thisMessageId));
                                                hasStartedStreaming = false;
                                                await handleToolCall(content);
                                                continue;
                                            }

                                            if (looksLikeToolCallPrefix(content)) {
                                                continue;
                                            }
                                            // Tool-type messages (retrieve_chroma_docs docs, tool outputs) are
                                            // internal context for the model — NEVER a user-facing reply. BMS
                                            // __TOOL_CALL__ markers were already caught above. (CoT leak fix.)
                                            if (msg.type === 'tool' || msg.role === 'tool') {
                                                continue;
                                            }
                                            // >>> LANGGRAPH CHATBOT INTEGRATION — Strip Kimi Markers (tool result) >>>
                                            if (content.includes('<|tool_calls_section_begin|>')) {
                                                kimiToolCallContent = content;
                                                content = content.split('<|tool_calls_section_begin|>')[0].trim();
                                            }
                                            // <<< LANGGRAPH CHATBOT INTEGRATION — Strip Kimi Markers (tool result) <<<

                                            thisStreamingContent = sanitizeStreamContent(content);
                                            if (thisStreamingContent && thisStreamingContent.trim()) {
                                                addThinkingPhase('Generating response');
                                            }
                                            if (!hasStartedStreaming && thisStreamingContent) {
                                                addMessage(thisStreamingContent, false, thisMessageId, null, null, null, true);
                                                trackAssistantMessage(thisMessageId);
                                                hasStartedStreaming = true;
                                            } else if (hasStartedStreaming) {
                                                updateMessage(thisMessageId, thisStreamingContent, null, null, true);
                                            }
                                        }
                                    }
                                }
                            }

                            if (currentEvent === 'messages/complete') {
                                const messages = Array.isArray(data) ? data : [data];
                                // Only process the LAST relevant message — earlier ones were handled during their own streams
                                let lastIdx = -1;
                                for (let i = messages.length - 1; i >= 0; i--) {
                                    if (isStreamToolMessage(messages[i])) { lastIdx = i; break; }
                                }
                                for (let i = 0; i < messages.length; i++) {
                                    if (i !== lastIdx) continue;
                                    const msg = messages[i];
                                    if (!isStreamToolMessage(msg)) continue;
                                    // AI message that ALSO carries a tool_call: still show any user-facing text
                                    // (the agent sometimes attaches the real answer / a failure notice to a tool
                                    // call). Pure tool_calls with no content fall through to the `if (content)`
                                    // guard below and display nothing. (Empty-reply fix.)

                                    let content = getStreamMessageContent(msg);
                                    if (content) {
                                        // Check for another nested __TOOL_CALL__ (multi-step)
                                        if (extractToolInstructionsFromContent(content)) {
                                            setChatMsg((prevChatMsg) => prevChatMsg.filter(m => m.messageId !== thisMessageId));
                                            hasStartedStreaming = false;
                                            await handleToolCall(content);
                                            continue;
                                        }
                                        // Tool-type messages (retrieve_chroma_docs docs, tool outputs) are
                                        // internal context — NEVER a user-facing reply. BMS __TOOL_CALL__
                                        // markers were already caught above. (CoT leak fix.)
                                        if (msg.type === 'tool' || msg.role === 'tool') {
                                            continue;
                                        }
                                        // Strip Kimi markers for clean display
                                        if (content.includes('<|tool_calls_section_begin|>')) {
                                            kimiToolCallContent = content;
                                            content = content.split('<|tool_calls_section_begin|>')[0].trim();
                                        }
                                        thisStreamingContent = sanitizeStreamContent(content);
                                        if (thisStreamingContent && thisStreamingContent.trim()) {
                                            addThinkingPhase('Generating response');
                                        }
                                        if (!hasStartedStreaming) {
                                            addMessage(thisStreamingContent, false, thisMessageId, null, null, null, false);
                                            trackAssistantMessage(thisMessageId);
                                            hasStartedStreaming = true;
                                        }
                                        updateMessage(thisMessageId, thisStreamingContent, citations, getCurrentTurnThinkingInfo(), false);
                                    }
                                }
                            }
                        } catch (e) {
                            console.warn('[Chatbot] Failed to parse tool result stream:', e);
                        }
                    }
                }
            }

            // If the conversation was cleared while a confirmation card was pending on a
            // nested hop, bail out instead of falling through to the state fetch / fallback
            // (prevents a stray 'I performed the requested action...' bubble after cancel).
            if (!threadId) { disarmToolStreamTimeout(); return; }

            // >>> LANGGRAPH CHATBOT INTEGRATION — Execute Deferred Kimi Tool Call (tool result) >>>
            if (kimiToolCallContent && kimiToolCallContent.includes('<|tool_call_end|>')) {
                const parsed = parseKimiToolCall(kimiToolCallContent);
                if (parsed) {
                    await handleToolCall(parsed);
                    return; // handleToolCall opens its own stream
                }
            }
            // <<< LANGGRAPH CHATBOT INTEGRATION — Execute Deferred Kimi Tool Call (tool result) <<<

            try {
                const stateResponse = await fetch(`${LANGGRAPH_URL}/threads/${threadId}/state`, {
                    headers: getAuthHeaders(),
                    mode: 'cors'
                });

                if (stateResponse.ok) {
                    const state = await stateResponse.json();
                    const messages = state.values?.messages || [];

                    for (let index = messages.length - 1; index >= 0; index -= 1) {
                        const msg = messages[index];
                        if (!isStreamToolMessage(msg)) {
                            continue;
                        }

                        const content = getStreamMessageContent(msg);
                        if (!content) {
                            continue;
                        }

                        if (extractToolInstructionsFromContent(content)) {
                            await handleToolCall(content);
                            return;
                        }

                        if (!hasStartedStreaming) {
                            addMessage(content, false, thisMessageId, citations, getCurrentTurnThinkingInfo());
                            trackAssistantMessage(thisMessageId);
                            hasStartedStreaming = true;
                        } else {
                            updateMessage(thisMessageId, content, citations, getCurrentTurnThinkingInfo(), false);
                        }
                        thisStreamingContent = content;
                        break;
                    }
                }
            } catch (recoveryError) {
                console.warn('[Chatbot] Failed to recover nested tool stream from thread state:', recoveryError);
            }

            // M3: if the tool-result stream ended with nothing displayed (the
            // "loaded for a long time then empty reply" symptom), show a visible
            // fallback so the user is never left staring at a blank turn. The
            // guard prevents double-firing across recursive calls (only the first
            // level with zero assistant messages this turn emits it).
            disarmToolStreamTimeout();
            if (hasStartedStreaming) {
                updateMessage(thisMessageId, thisStreamingContent, citations, getCurrentTurnThinkingInfo(), false);
            } else if (threadId && !currentTurnAssistantMessageIdsRef.current.some(id => typeof id === 'string' && id.endsWith('-fallback'))) {
                // EMPTY-REPLY FIX: this terminal tool-result round-trip produced no
                // displayable assistant text (the run emitted nothing, or its only AI
                // message carried a tool_call and was suppressed). The previous guard
                // (...length === 0) skipped this fallback whenever earlier steps in the
                // same turn had already shown messages — leaving a blank bubble on long
                // flows (e.g. open→edit→save). Now we always surface a visible reply,
                // deduped per turn via the '-fallback' id suffix.
                addMessage(
                    'I performed the requested action in BMS, but the final confirmation did not come through clearly. ' +
                    'If you asked me to save or change something, it may not have been saved — a save that reports a ' +
                    '"Network Error" means the BMS report/document service is unavailable. Would you like me to re-open ' +
                    'the Shipping Order and verify the current status?',
                    false,
                    `${thisMessageId}-fallback`
                );
                trackAssistantMessage(`${thisMessageId}-fallback`);
            }
            setDisableInput(false);
            hideProcessingIndicator();

        } catch (error) {
            disarmToolStreamTimeout();
            console.error('[Chatbot] Error sending tool result:', error);
            setDisableInput(false);
            showError('Failed to execute tool. Please try again.');
        }
    }
    // <<< LANGGRAPH CHATBOT INTEGRATION — Tool Call Handler <<<

    const sendMessage = async (message, fileAttachments = []) => {
        if (!threadId) {
            showError('No active conversation thread');
            return;
        }

        currentTurnAssistantMessageIdsRef.current = [];
        preparedFormChangesRef.current = [];
        beginCurrentTurnThinking();

        // Add user message to conversation history (multimodal if attachments)
        const msgContent = fileAttachments && fileAttachments.length > 0
            ? await _buildMultimodalContent(message, fileAttachments)
            : message;
        conversationHistory.push({
            role: 'user',
            content: msgContent
        });

        // Create a unique message ID for THIS specific message send
        // This prevents race conditions when multiple messages are sent quickly
        const thisMessageId = 'streaming-message-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
        let thisStreamingContent = '';

        // Track the number of messages BEFORE sending to detect new responses
        const messageCountBeforeSend = chatMsg.filter(m => !m.isUser).length;

        try {
            console.log('Sending message to LangGraph:', {
                url: `${LANGGRAPH_URL}/threads/${threadId}/runs/stream`,
                threadId: threadId,
                assistant_id: ASSISTANT_ID,
                messageCount: conversationHistory.length,
                message: message,
                messageId: thisMessageId
            });

            const response = await fetch(
                `${LANGGRAPH_URL}/threads/${threadId}/runs/stream`,
                {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    mode: 'cors',
                    body: JSON.stringify({
                        assistant_id: ASSISTANT_ID,
                        input: {
                            messages: conversationHistory
                        },
                        stream_mode: ['messages', 'updates']
                    })
                }
            );

            // Handle thread not found (404) - recreate thread
            if (response.status === 404) {
                console.warn('Thread not found, creating new thread...');
                hideTypingIndicator();
                showError('Session expired. Creating new conversation...');

                // Clear old thread and create new one
                threadId = null;
                sessionStorage.removeItem('langgraph_thread_id');
                const newThreadId = await initializeChat({ forceNew: true });

                if (newThreadId) {
                    // Retry sending the message with new thread
                    showTypingIndicator();
                    setTimeout(() => sendMessage(message, fileAttachments), 500);
                }
                return;
            }

            if (!response.ok) {
                const errorText = await response.text().catch(() => response.statusText);
                console.error('API Error:', response.status, errorText);
                throw new Error(`Failed to send message: ${response.statusText}`);
            }

            // Remove typing indicator and create a message placeholder for streaming
            hideTypingIndicator();
            showProcessingIndicator();
            streamingContent = '';
            let hasStartedStreaming = false;
            let kimiToolCallContent = null; // >>> LANGGRAPH CHATBOT INTEGRATION — Deferred Kimi tool call
            let streamTimeout = null;
            let toolChainInitiated = false; // Track if handleToolCall was called to skip recovery

            // Set a timeout for stream completion (120 seconds).
            // Kimi K2.5 via Foundry MaaS buffers (no token streaming), so the
            // frontend sees no data until the full response is ready. Multi-hop
            // BMS tool chains (navigate → search → read → answer) legitimately
            // run 20-40s. 60s was too aggressive and showed false timeout errors.
            streamTimeout = setTimeout(() => {
                if (!hasStartedStreaming) {
                    console.error('Stream timeout: No response received for message', thisMessageId);
                    hideProcessingIndicator();
                    showError('The assistant is taking too long to respond. Please try again.');
                }
            }, 180000);

            // Process streaming response (Server-Sent Events format)
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let currentEvent = '';
            let citations = [];

            // Thinking/phase tracking
            let thinkingPhases = [];
            let totalReasoningTokens = 0;
            let lastPhase = '';
            const addThinkingPhase = (phase) => {
                if (phase && phase !== lastPhase) {
                    thinkingPhases.push(phase);
                    lastPhase = phase;
                    setThinkingPhase(phase);
                    addCurrentTurnPhase(phase);
                }
            };
            addThinkingPhase('Analyzing your question');

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    // Clear the stream timeout when done
                    if (streamTimeout) clearTimeout(streamTimeout);
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');

                // Keep the last incomplete line in buffer
                buffer = lines.pop() || '';

                for (const line of lines) {
                    // Parse SSE format: "event: <type>" and "data: <json>"
                    if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    }
                    else if (line.startsWith('data: ')) {
                        try {
                            const jsonStr = line.slice(6).trim();
                            if (!jsonStr) continue;

                            const data = JSON.parse(jsonStr);

                            //console.log('Event:', currentEvent, 'Data:', data);

                            // Handle updates event to capture metadata/citations
                            if (currentEvent === 'updates') {
                                console.log('Updates event received:', JSON.stringify(data, null, 2));

                                // Multi-agent plan + cursor. planner emits agent_plan (>=2 entries)
                                // and current_agent_idx=0 once at turn start; handoff advances
                                // current_agent_idx before each subsequent agent streams. The agent
                                // currently streaming is INTERMEDIATE unless its idx is the last.
                                if (data?.planner?.agent_plan?.length >= 2) {
                                    currentTurnAgentPlanRef.current = data.planner.agent_plan;
                                    currentTurnAgentIdxRef.current = data.planner.current_agent_idx ?? 0;
                                }
                                if (data?.handoff && typeof data.handoff.current_agent_idx === 'number') {
                                    currentTurnAgentIdxRef.current = data.handoff.current_agent_idx;
                                }

                                // Phase detection: tool execution
                                // Check for both old flat architecture (tool_node) and
                                // subgraph architecture (rag_subgraph, bms_subgraph, etc.)
                                const hasToolNode = data && (data.tool_node || data.rag_subgraph || data.bms_subgraph);
                                if (hasToolNode) {
                                    addThinkingPhase('Retrieving relevant documents');
                                }

                                // Check if the update contains metadata - try different paths
                                if (data && data.tool_node) {
                                    console.log('Processing tool_node event');

                                    // Check metadata
                                    if (data.tool_node.metadata) {
                                        console.log('Found tool_node.metadata:', JSON.stringify(data.tool_node.metadata, null, 2));
                                        citations = data.tool_node.metadata;
                                        console.log('Captured citations from tool_node.metadata:', citations);
                                    }

                                    // Check artifact field
                                    if (data.tool_node.artifact) {
                                        console.log('Found tool_node.artifact:', JSON.stringify(data.tool_node.artifact, null, 2));
                                        try {
                                            const artifactContent = typeof data.tool_node.artifact === 'string'
                                                ? JSON.parse(data.tool_node.artifact)
                                                : data.tool_node.artifact;
                                            console.log('Parsed artifact content:', artifactContent);

                                            // Check if artifact contains citations/documents
                                            if (artifactContent && (artifactContent.documents || artifactContent.citations)) {
                                                citations = artifactContent.documents || artifactContent.citations;
                                                console.log('Captured citations from artifact:', citations);
                                            }
                                        } catch (e) {
                                            console.log('Artifact is not JSON, raw value:', data.tool_node.artifact);
                                        }
                                    }
                                }
                                else if (data && data.metadata) {
                                    citations = data.metadata;
                                    console.log('Captured citations from data.metadata:', citations);
                                }
                                else if (data && typeof data === 'object') {
                                    // Try to find metadata anywhere in the object
                                    const searchForMetadata = (obj, path = '') => {
                                        if (!obj || typeof obj !== 'object') return;
                                        if (Array.isArray(obj)) {
                                            obj.forEach((item, idx) => searchForMetadata(item, `${path}[${idx}]`));
                                            return;
                                        }
                                        if (obj.metadata) {
                                            console.log(`Found metadata at ${path}:`, obj.metadata);
                                            if (Array.isArray(obj.metadata) && obj.metadata.length > 0) {
                                                citations = obj.metadata;
                                            }
                                        }
                                        // Also check for citations directly (not in metadata)
                                        if (obj.citations) {
                                            console.log(`Found citations at ${path}:`, obj.citations);
                                            if (Array.isArray(obj.citations) && obj.citations.length > 0) {
                                                citations = obj.citations;
                                            }
                                        }
                                        // Check for documents array
                                        if (obj.documents) {
                                            console.log(`Found documents at ${path}:`, obj.documents);
                                            if (Array.isArray(obj.documents) && obj.documents.length > 0) {
                                                citations = obj.documents;
                                            }
                                        }
                                        Object.keys(obj).forEach(key => {
                                            if (key !== 'metadata' && key !== 'citations' && key !== 'documents') {
                                                searchForMetadata(obj[key], `${path}.${key}`);
                                            }
                                        });
                                    };
                                    searchForMetadata(data, 'data');
                                }

                                // Final check: log what we ended up with
                                console.log('Final citations after extraction:', citations);
                                console.log('Citations length:', citations ? citations.length : 0);
                                // If the answer is already displayed, re-render with citations
                                // immediately (updates for subgraph nodes fire AFTER messages/complete).
                                if (citations && citations.length > 0 && hasStartedStreaming && thisStreamingContent) {
                                    updateMessage(thisMessageId, thisStreamingContent, citations);
                                }
                            }

                            // Handle messages/partial for streaming updates (token by token)
                            if (currentEvent === 'messages/partial') {
                                const messages = Array.isArray(data) ? data : [data];

                                for (const msg of messages) {
                                    // Check if this is an AI message
                                    if (isStreamToolMessage(msg)) {
                                        let content = getStreamMessageContent(msg);

                                        // Phase detection: tool calls = searching
                                        if (msg.tool_calls && msg.tool_calls.length > 0 && !content) {
                                            addThinkingPhase('Searching knowledge base');
                                        }

                                        // If this partial has native tool_calls AND content, suppress display
                                        // The content is just a preamble like "I'll help you..." — the real action is the tool call
                                        if (msg.tool_calls && msg.tool_calls.length > 0 && content) {
                                            if (hasStartedStreaming && isFinalAgentStreaming()) {
                                                setChatMsg((prevChatMsg) => prevChatMsg.filter(m => m.messageId !== thisMessageId));
                                                hasStartedStreaming = false;
                                            }
                                            toolChainInitiated = true;
                                            if (streamTimeout) clearTimeout(streamTimeout);
                                            continue;
                                        }

                                        // Extract reasoning tokens from usage_metadata
                                        if (msg.usage_metadata?.output_token_details?.reasoning) {
                                            totalReasoningTokens += msg.usage_metadata.output_token_details.reasoning;
                                            addCurrentTurnReasoningTokens(msg.usage_metadata.output_token_details.reasoning);
                                        }
                                        addCurrentTurnReasoningText(getStreamReasoningContent(msg));

                                        // Update on every chunk for real-time streaming
                                        if (content !== undefined && content !== thisStreamingContent) {
                                            if (extractToolInstructionsFromContent(content)) {
                                                setChatMsg((prevChatMsg) => prevChatMsg.filter(m => m.messageId !== thisMessageId));
                                                hasStartedStreaming = false;
                                                toolChainInitiated = true;
                                                if (streamTimeout) clearTimeout(streamTimeout);
                                                await handleToolCall(content);
                                                return;
                                            }

                                            if (looksLikeToolCallPrefix(content)) {
                                                continue;
                                            }

                                            // Tool-type messages (retrieve_chroma_docs docs, tool outputs) are
                                            // internal context for the model — NEVER a user-facing reply. BMS
                                            // __TOOL_CALL__ markers were already caught above. (CoT leak fix.)
                                            if (msg.type === 'tool' || msg.role === 'tool') {
                                                continue;
                                            }

                                            // >>> LANGGRAPH CHATBOT INTEGRATION — Strip Kimi Markers >>>
                                            // Kimi-K2.5 outputs tool calls as raw text tokens.
                                            // Strip them here for clean display; execute tool calls AFTER stream ends.
                                            if (content.includes('<|tool_calls_section_begin|>')) {
                                                kimiToolCallContent = content; // Save for post-loop execution
                                                content = content.split('<|tool_calls_section_begin|>')[0].trim();
                                            }
                                            // <<< LANGGRAPH CHATBOT INTEGRATION — Strip Kimi Markers <<<

                                            thisStreamingContent = sanitizeStreamContent(content);

                                            // Multi-agent: route an INTERMEDIATE agent's streamed text into the
                                            // thinking trace instead of a reply bubble. hasStartedStreaming is
                                            // never set here, so no bubble is ever created for the RAG answer
                                            // (which the next agent would otherwise flash-then-overwrite).
                                            if (thisStreamingContent && thisStreamingContent.trim() && !isFinalAgentStreaming()) {
                                                addThinkingPhase(currentAgentLabel());
                                                addCurrentTurnAgentStep(currentAgentLabel(), thisStreamingContent);
                                                continue;
                                            }

                                            // Phase detection: content = generating response
                                            if (thisStreamingContent && thisStreamingContent.trim()) {
                                                addThinkingPhase('Generating response');
                                            }

                                            // First token received - replace processing indicator with message
                                            if (!hasStartedStreaming) {
                                                // Only add message and hide processing indicator if we have actual content
                                                if (thisStreamingContent && thisStreamingContent.trim().length > 0) {
                                                    addMessage(thisStreamingContent, false, thisMessageId, null, null, null, true);
                                                    trackAssistantMessage(thisMessageId);
                                                    hasStartedStreaming = true;
                                                }
                                            }
                                            else {
                                                updateMessage(thisMessageId, thisStreamingContent, null, null, true);
                                            }
                                        }
                                    }
                                }
                            }

                            // Handle messages/complete for final message
                            if (currentEvent === 'messages/complete') {
                                const messages = Array.isArray(data) ? data : [data];
                                // Only process the LAST relevant message — earlier ones were handled during their own streams
                                let lastIdx = -1;
                                for (let i = messages.length - 1; i >= 0; i--) {
                                    if (isStreamToolMessage(messages[i])) { lastIdx = i; break; }
                                }

                                for (let i = 0; i < messages.length; i++) {
                                    if (i !== lastIdx) continue;
                                    const msg = messages[i];
                                    if (!isStreamToolMessage(msg)) continue;
                                    // AI message that ALSO carries a tool_call: still show any user-facing text
                                    // (the agent sometimes attaches the real answer / a failure notice to a tool
                                    // call). Pure tool_calls with no content fall through to the `if (content)`
                                    // guard below and display nothing. (Empty-reply fix.)

                                    let content = getStreamMessageContent(msg);

                                    if (content) {
                                        // >>> LANGGRAPH CHATBOT INTEGRATION — Tool Call Check >>>
                                        if (extractToolInstructionsFromContent(content)) {
                                            console.log('[Chatbot] Received tool call from server');
                                            setChatMsg((prevChatMsg) => prevChatMsg.filter(m => m.messageId !== thisMessageId));
                                            hasStartedStreaming = false;
                                            toolChainInitiated = true;
                                            if (streamTimeout) clearTimeout(streamTimeout);
                                            const handled = await handleToolCall(content);
                                            if (handled) {
                                                continue; // Don't display tool call as chat message
                                            }
                                        }
                                        // Tool-type messages (retrieve_chroma_docs docs, tool outputs) are
                                        // internal context — NEVER a user-facing reply. BMS __TOOL_CALL__
                                        // markers were already caught above. (CoT leak fix.)
                                        if (msg.type === 'tool' || msg.role === 'tool') {
                                            continue;
                                        }
                                        // Strip Kimi markers for clean display
                                        if (content.includes('<|tool_calls_section_begin|>')) {
                                            kimiToolCallContent = content;
                                            content = content.split('<|tool_calls_section_begin|>')[0].trim();
                                        }
                                        // <<< LANGGRAPH CHATBOT INTEGRATION — Tool Call Check <<<

                                        thisStreamingContent = sanitizeStreamContent(content);

                                        // Multi-agent: keep intermediate agent output in the trace, not a bubble.
                                        if (thisStreamingContent && thisStreamingContent.trim() && !isFinalAgentStreaming()) {
                                            addThinkingPhase(currentAgentLabel());
                                            addCurrentTurnAgentStep(currentAgentLabel(), thisStreamingContent);
                                            continue;
                                        }

                                        // First content received - replace processing indicator
                                        if (!hasStartedStreaming) {
                                            // Only add message and hide processing indicator if we have actual content
                                            if (thisStreamingContent && thisStreamingContent.trim().length > 0) {
                                                addMessage(thisStreamingContent, false, thisMessageId, null, null, null, false);
                                                trackAssistantMessage(thisMessageId);
                                                hasStartedStreaming = true;
                                            }
                                        }

                                        // Update with final content and citations
                                        updateMessage(thisMessageId, thisStreamingContent, citations, null, false);
                                        console.log('Updated message with citations:', citations.length, 'citations');
                                    }
                                }
                            }
                        }
                        catch (e) {
                            console.warn('Failed to parse streaming data:', line, e);
                        }
                    }
                }
            }

            // >>> LANGGRAPH CHATBOT INTEGRATION — Execute Deferred Kimi Tool Call >>>
            if (kimiToolCallContent && kimiToolCallContent.includes('<|tool_call_end|>')) {
                const parsed = parseKimiToolCall(kimiToolCallContent);
                if (parsed) {
                    toolChainInitiated = true;
                    if (streamTimeout) clearTimeout(streamTimeout);
                    hideProcessingIndicator();
                    await handleToolCall(parsed);
                    return; // handleToolCall opens its own stream, skip rest of sendMessage
                }
            }
            // <<< LANGGRAPH CHATBOT INTEGRATION — Execute Deferred Kimi Tool Call <<<

            // After the stream is complete, build thinking info
            const thinkingInfo = getCurrentTurnThinkingInfo() || (thinkingPhases.length > 0 ? {
                phases: thinkingPhases,
                reasoningTokens: totalReasoningTokens,
                duration: Date.now() - (processingStartTimeRef.current || Date.now())
            } : null);

            // Apply final citations and thinking info if we have them
            if (hasStartedStreaming && citations && citations.length > 0) {
                console.log('Stream complete - applying citations:', citations);
                updateMessage(thisMessageId, thisStreamingContent, citations, thinkingInfo, false);
            } else if (hasStartedStreaming && thinkingInfo) {
                updateMessage(thisMessageId, thisStreamingContent, undefined, thinkingInfo, false);
            }

            // CRITICAL FIX: Ensure message was always added, even if streaming had issues
            hideProcessingIndicator();

            // If a tool chain was initiated (handleToolCall was called), skip recovery —
            // the tool chain handles its own display via sendToolResultToServer
            if (toolChainInitiated) {
                console.log('Tool chain was initiated, skipping recovery section');
            } else if (!hasStartedStreaming) {
                console.warn('Stream completed but hasStartedStreaming is false. ThisStreamingContent:', thisStreamingContent);
                // Fallback if no content was streamed
                if (!thisStreamingContent) {
                    console.log('No message in stream, fetching thread state...');
                    try {
                        const stateResponse = await fetch(`${LANGGRAPH_URL}/threads/${threadId}/state`, {
                            headers: getAuthHeaders(),
                            mode: 'cors'
                        });
                        if (stateResponse.ok) {
                            const state = await stateResponse.json();
                            console.log('Thread state:', state);

                            // Check for citations in the state
                            if (state.values && state.values.metadata) {
                                citations = state.values.metadata;
                                console.log('Found citations in state.values.metadata:', citations);
                            }

                            if (state.values && state.values.messages) {
                                const messages = state.values.messages;
                                // Count AI messages in thread state
                                const aiMessagesInState = messages.filter(m => m.type === 'ai' || m.role === 'assistant');

                                // Only try to recover if there are MORE AI messages in state than we've displayed
                                if (aiMessagesInState.length > messageCountBeforeSend) {
                                    // There's a new message in the state that we haven't displayed yet
                                    for (let i = messages.length - 1; i >= 0; i--) {
                                        const msg = messages[i];
                                        if (isStreamToolMessage(msg)) {
                                            const content = getStreamMessageContent(msg);
                                            if (content) {
                                                if (extractToolCallFromContent(content)) {
                                                    continue;
                                                }
                                                thisStreamingContent = content;
                                                addMessage(thisStreamingContent, false, thisMessageId, citations, thinkingInfo);
                                                hasStartedStreaming = true;
                                                console.log('Recovered NEW message from thread state (total AI messages:', aiMessagesInState.length, ', displayed:', messageCountBeforeSend, ')');
                                                return;
                                            }
                                        }
                                    }
                                } else {
                                    console.warn('No new messages in thread state (total AI messages:', aiMessagesInState.length, ', already displayed:', messageCountBeforeSend, ')');
                                    console.warn('The stream did not contain a new response. This may indicate a backend issue.');
                                    // EMPTY-REPLY FIX: the turn ended with no clean text reply — often the last
                                    // AI message carried a tool_call (so it was suppressed above) or the run
                                    // produced no message at all. Recover the last AI message's text content
                                    // from state — even if it also had tool_calls — so the user never sees a
                                    // blank bubble. Strip __TOOL_CALL__ / Kimi tool-call markers first.
                                    for (let i = messages.length - 1; i >= 0; i--) {
                                        const m = messages[i];
                                        if (!(m.type === 'ai' || m.role === 'assistant')) continue;
                                        let c = typeof m.content === 'string'
                                            ? m.content
                                            : (Array.isArray(m.content) ? m.content.filter(b => b && b.type === 'text').map(b => b.text).join('') : '');
                                        if (!c) continue;
                                        if (c.includes('<|tool_calls_section_begin|>')) {
                                            c = c.split('<|tool_calls_section_begin|>')[0];
                                        }
                                        c = c.replace(/__TOOL_CALL__:[\s\S]*$/,'').trim();
                                        if (c) {
                                            addMessage(c, false, thisMessageId, citations, thinkingInfo);
                                            hasStartedStreaming = true;
                                            console.log('Recovered last AI text content (tool-call-attached) to avoid blank reply');
                                            return;
                                        }
                                    }
                                    // Nothing recoverable — surface a notice instead of a blank bubble.
                                    addMessage("I processed your request but couldn't produce a final response. If you asked me to save or change something, the action may have failed (e.g. a backend 'Network Error'). Please try again or rephrase.", false, thisMessageId);
                                    hasStartedStreaming = true;
                                    return;
                                }
                            }
                        }
                    }
                    catch (e) {
                        console.error('Failed to fetch thread state:', e);
                    }

                    // Last resort - add error message only if we haven't already displayed something
                    if (!hasStartedStreaming) {
                        console.error('Unable to recover message content');
                        addMessage("I received your message but couldn't generate a proper response. Please try again.", false, thisMessageId);
                    }
                } else {
                    // Content exists but was never added to UI - add it now
                    console.log('Content exists but was never displayed, adding now:', thisStreamingContent.substring(0, 100));
                    addMessage(thisStreamingContent, false, thisMessageId, citations, thinkingInfo);
                    hasStartedStreaming = true;
                }
            } else {
                // Stream was successful - ensure final update includes citations
                console.log('Final update - checking for citations:', citations);

                // Fallback: if citations were not captured from stream, fetch from thread state
                if (!citations || citations.length === 0) {
                    console.log('No citations from stream, fetching from thread state...');
                    try {
                        const stateResponse = await fetch(`${LANGGRAPH_URL}/threads/${threadId}/state`, {
                            headers: getAuthHeaders(),
                            mode: 'cors'
                        });
                        if (stateResponse.ok) {
                            const state = await stateResponse.json();
                            if (state.values && state.values.metadata && Array.isArray(state.values.metadata) && state.values.metadata.length > 0) {
                                citations = state.values.metadata;
                                console.log('Recovered citations from thread state:', citations.length, 'citations');
                            }
                        }
                    } catch (e) {
                        console.error('Failed to fetch thread state for citations:', e);
                    }
                }

                if (citations && citations.length > 0) {
                    updateMessage(thisMessageId, thisStreamingContent, citations, thinkingInfo, false);
                }

                // Add AI response to conversation history
                if (thisStreamingContent) {
                    conversationHistory.push({
                        role: 'assistant',
                        content: thisStreamingContent
                    });
                    console.log('Conversation history now has', conversationHistory.length, 'messages');
                }
            }

        }
        catch (error) {
            console.error('Error sending message:', error);
            hideTypingIndicator();
            hideProcessingIndicator();

            // More specific error message
            if (error.name === 'AbortError') {
                showError('Connection timed out. Please try again.');
            }
            else if (error.message.includes('404')) {
                showError('Session expired. Please refresh and try again.');
            }
            else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                showError('Network error. Please check your connection and try again.');
            }
            else {
                showError('Failed to send message. Please try again.');
            }
        }
        finally {
            // No need to reset global variables since we're using local variables now
        }
    };

    const toggleChat = async () => {
        isOpen = !isOpen;
        chatBubble.classList.toggle('active');
        chatWindow.classList.toggle('active');

        if (isOpen) {
            try {
                const activeThreadId = await initializeChat();
                if (!activeThreadId) {
                    showError('Unable to connect to the assistant. Please try again.');
                }
            }
            catch (error) {
                console.error('Failed to initialize chat:', error);
                showError('Failed to connect to the assistant. Please try again.');
                setChatInputEnabled(true, 'Connection failed. Retry...');
            }

            if (isOpen && inputRef && inputRef.current) {
                inputRef.current.focus();
            }
        }
        else {
            // Clear conversation when closing the chat
            clearConversation();
            updateConnectionStatus('idle');
        }
    };

    const minimizeChat = () => {
        // Just hide the chat window without clearing conversation
        isOpen = false;
        chatBubble.classList.remove('active');
        chatWindow.classList.remove('active');
        // Keep the connection status and conversation history intact
        // Don't call clearConversation() or updateConnectionStatus('idle')
    };

    const initializeChat = async (options = {}) => {
        const normalizedOptions = typeof options === 'boolean' ? { forceNew: options } : options;
        const {
            forceNew = false,
            silent = false
        } = normalizedOptions;

        if (threadId && !forceNew) {
            if (!silent) {
                updateConnectionStatus('online');
                setChatInputEnabled(true);
            }
            return threadId;
        }

        if (isInitializingThread) {
            return threadInitializationPromise;
        }

        isInitializingThread = true;

        const shouldUpdateUi = !silent;
        if (shouldUpdateUi) {
            updateConnectionStatus('connecting');
            if (!threadId || forceNew) {
                setChatInputEnabled(false, forceNew ? 'Starting new chat...' : 'Connecting to assistant...');
            }
        }

        threadInitializationPromise = (async () => {
            if (!forceNew) {
                const savedThreadId = sessionStorage.getItem('langgraph_thread_id');
                if (savedThreadId) {
                    //console.log('Restoring thread from session:', savedThreadId);
                    try {
                        const exists = await verifyThreadExists(savedThreadId);
                        if (exists) {
                            threadId = savedThreadId;
                            //console.log('Thread restored successfully');
                            return threadId;
                        }
                        console.warn('Saved thread no longer exists, creating new one');
                        sessionStorage.removeItem('langgraph_thread_id');
                    }
                    catch (error) {
                        console.error('Failed to verify existing thread:', error);
                        throw error;
                    }
                }
            }
            else {
                sessionStorage.removeItem('langgraph_thread_id');
            }

            try {
                const newThreadId = await createThread();
                if (!newThreadId) {
                    throw new Error('Thread ID missing from createThread response');
                }
                threadId = newThreadId;
                //console.log('Chat initialized with new thread:', threadId);
                return threadId;
            }
            catch (error) {
                console.error('Failed to create new thread:', error);
                throw error;
            }
        })();

        try {
            const id = await threadInitializationPromise;
            if (!id) {
                if (shouldUpdateUi) {
                    setChatInputEnabled(true, 'Connection failed. Retry...');
                    updateConnectionStatus('error');
                }
                return null;
            }

            if (shouldUpdateUi) {
                updateConnectionStatus('online');
                setChatInputEnabled(true);
            }
            return id;
        }
        catch (error) {
            if (shouldUpdateUi) {
                const placeholder = error && error.name === 'AbortError'
                    ? 'Connection timed out. Retry...'
                    : 'Connection failed. Retry...';
                setChatInputEnabled(true, placeholder);
                updateConnectionStatus('error');
            } else {
                console.warn('Chat initialization failed (silent mode):', error);
            }
            return null;
        }
        finally {
            isInitializingThread = false;
            threadInitializationPromise = null;
        }
    };

    const clearConversation = () => {
        rejectAllPendingConfirmations();
        // Reset thread ID
        threadId = null;
        sessionStorage.removeItem('langgraph_thread_id');
        setChatInputEnabled(true);

        if (inputRef && inputRef.current) {
            inputRef.current.value = '';
        }

        // Clear conversation history
        conversationHistory = [];
        console.log('Conversation history cleared');

        // Clear the React message state. The welcome message + suggested-prompt
        // chips are rendered by React from the (now empty) state — see the JSX.
        // Previously this reset chatMessages.innerHTML directly, which fought
        // React: it clobbered the DOM but left chatMsg populated, so on reopen
        // the old conversation was visually gone yet the empty-state chips never
        // reappeared. Letting React own the render keeps state and DOM in sync.
        setChatMsg([]);

        updateConnectionStatus('idle');
    };

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const MAX_ATTACHMENTS = 5;
    const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'];

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);

        for (const file of files) {
            if (attachments.length >= MAX_ATTACHMENTS) {
                showError('Maximum ' + MAX_ATTACHMENTS + ' attachments allowed');
                break;
            }
            if (file.size > MAX_FILE_SIZE) {
                showError('File "' + file.name + '" exceeds 10MB limit');
                continue;
            }
            if (!ALLOWED_TYPES.includes(file.type)) {
                showError('File type "' + file.type + '" not supported. Use images or PDFs.');
                continue;
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                const base64Data = event.target.result;
                const isImage = file.type.startsWith('image/');
                setAttachments(prev => [...prev, {
                    file,
                    name: file.name,
                    type: isImage ? 'image' : 'pdf',
                    mimeType: file.type,
                    base64: base64Data,
                    preview: isImage ? base64Data : null
                }]);
            };
            reader.readAsDataURL(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const resizeImage = (base64Data, maxWidth = 1600) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                if (img.width <= maxWidth) {
                    resolve(base64Data);
                    return;
                }
                const canvas = document.createElement('canvas');
                const ratio = maxWidth / img.width;
                canvas.width = maxWidth;
                canvas.height = Math.round(img.height * ratio);
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/jpeg', 0.85));
            };
            img.src = base64Data;
        });
    };

    const _buildMultimodalContent = async (message, fileAttachments) => {
        if (!fileAttachments || fileAttachments.length === 0) {
            return message;
        }
        const contentBlocks = [];
        if (message) {
            contentBlocks.push({ type: 'text', text: message });
        }
        for (const att of fileAttachments) {
            if (att.type === 'image') {
                const resizedBase64 = await resizeImage(att.base64);
                contentBlocks.push({
                    type: 'image_url',
                    image_url: { url: resizedBase64 }
                });
            } else if (att.type === 'pdf') {
                contentBlocks.push({
                    type: 'text',
                    text: '__PDF_ATTACHMENT__:' + att.name + ':' + att.base64
                });
            }
        }
        return contentBlocks;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (e && e.type !== 'click' && e.type !== 'submit' && e.type !== 'keydown') return;
        if (!inputRef || !inputRef.current) return;

        const { current } = inputRef;
        const message = current.value.trim();
        const currentAttachments = attachments;

        // Require either text or attachments
        if (!message && currentAttachments.length === 0) return;

        if (!(await initializeChat())) {
            showError('Unable to connect to the assistant. Please try again.');
            return;
        }

        // Build attachment info for display
        const displayAttachments = currentAttachments.map(att => ({
            type: att.type,
            name: att.name,
            preview: att.preview,
            base64: att.base64
        }));

        // Add user message to chat (with attachment info for display)
        addMessage(message || '📄 Attachment(s)', true, null, null, null, displayAttachments);
        current.value = '';

        // Clear attachments immediately (already captured in currentAttachments above)
        setAttachments([]);

        // Show typing indicator
        showTypingIndicator();

        // Send message with attachments
        await sendMessage(message, currentAttachments);
    };

    // Clicking a suggested-prompt chip sends its full `prompt` text exactly like a
    // typed send (no attachments). Disabled while a turn is in flight.
    const sendSuggestedPrompt = async (prompt) => {
        const message = (prompt || '').trim();
        if (!message || disableInput) return;

        if (!(await initializeChat())) {
            showError('Unable to connect to the assistant. Please try again.');
            return;
        }

        addMessage(message, true);
        showTypingIndicator();
        await sendMessage(message, []);
    };

    const normalizeMarkdownTables = (content) => {
        if (typeof content !== 'string') {
            return content;
        }

        const trimmed = content.trim();
        const isSingleLineTable = !trimmed.includes('\n')
            && trimmed.startsWith('|')
            && /\|\s*:?-{3,}/.test(trimmed);

        if (!isSingleLineTable) {
            return content;
        }

        // Some model responses collapse table rows into one line: "| col | col | |---|---| | ... |".
        return trimmed.replace(/\|\s+\|/g, '|\n|');
    };

    // Split a table row into trimmed cells, tolerating OPTIONAL leading/trailing
    // pipes so both GFM formats parse: "| a | b |" and "a | b" -> ["a","b"].
    const parseTableRow = (line) => {
        const inner = line.trim().replace(/^\s*\|/, '').replace(/\|\s*$/, '');
        return inner.split('|').map((cell) => cell.trim());
    };
    const normalizeTableRow = (row, columnCount) => {
        let normalized = [...row];

        if (normalized.length === columnCount + 1 && normalized[0] === '') {
            normalized = normalized.slice(1);
        }
        if (normalized.length === columnCount + 1 && normalized[normalized.length - 1] === '') {
            normalized = normalized.slice(0, -1);
        }

        if (normalized.length < columnCount) {
            return [...normalized, ...new Array(columnCount - normalized.length).fill('')];
        }
        if (normalized.length > columnCount) {
            return [
                ...normalized.slice(0, columnCount - 1),
                normalized.slice(columnCount - 1).join(' | ')
            ];
        }

        return normalized;
    };
    // Divider row = cells of only dashes/colons separated by pipes, outer pipes
    // optional. e.g. "---|---|---" and "| --- | --- |" both qualify.
    const isTableDivider = (line) => {
        const trimmed = line.trim();
        if (!trimmed.includes('|')) return false;
        const cells = parseTableRow(trimmed);
        return cells.length >= 2
            && cells.every((cell) => /^\s*:?-+:?\s*$/.test(cell) && cell.includes('-'));
    };
    // A potential table row = any non-empty line containing a pipe. The divider
    // on the following line is the authoritative table signal, so a stray "|"
    // inside ordinary prose will not trigger a table.
    const isTableRow = (line) => {
        const trimmed = line.trim();
        return trimmed.length > 0 && trimmed.includes('|');
    };

    const splitMarkdownContent = (content) => {
        const normalized = normalizeMarkdownTables(content);
        if (typeof normalized !== 'string') {
            return [{ type: 'markdown', content: '' }];
        }

        const lines = normalized.split('\n');
        const blocks = [];
        let markdownBuffer = [];

        const flushMarkdown = () => {
            if (markdownBuffer.length) {
                blocks.push({
                    type: 'markdown',
                    content: markdownBuffer.join('\n')
                });
                markdownBuffer = [];
            }
        };

        let index = 0;
        while (index < lines.length) {
            const currentLine = lines[index];
            const trimmedLine = currentLine.trim();

            if (
                isTableRow(trimmedLine)
                && index + 1 < lines.length
                && isTableDivider(lines[index + 1])
            ) {
                const headers = parseTableRow(trimmedLine);
                let rowIndex = index + 2;
                const rows = [];

                while (rowIndex < lines.length) {
                    const rowLine = lines[rowIndex].trim();
                    if (!isTableRow(rowLine)) {
                        break;
                    }

                    const row = normalizeTableRow(parseTableRow(rowLine), headers.length);
                    rows.push(row);
                    rowIndex += 1;
                }

                if (rows.length) {
                    flushMarkdown();
                    blocks.push({ type: 'table', headers, rows });
                    index = rowIndex;
                    continue;
                }
            }

            markdownBuffer.push(currentLine);
            index += 1;
        }

        flushMarkdown();

        return blocks.length ? blocks : [{ type: 'markdown', content: normalized }];
    };

    // Role gate: render nothing (no bubble, no window) for roles outside the allow-list.
    //if (!isChatbotAllowedForCurrentUser()) {
    //    return null;
    //}

    return (
        <div>
            {lightbox && (
                <div className="da-lightbox" onClick={() => setLightbox(null)}>
                    {lightbox.kind === 'img'
                        ? <img src={lightbox.src} alt="chart" />
                        : <DAChart spec={lightbox.spec} fullscreen />}
                </div>
            )}
            <button id="chatBubble" className="chat-bubble" aria-label="Open chat">
                <svg className="chat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                </svg>
                <svg className="close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>

            <div id="chatWindow" className="chat-window">
                <div className="resize-handle resize-top"></div>
                <div className="resize-handle resize-right"></div>
                <div className="resize-handle resize-bottom"></div>
                <div className="resize-handle resize-left"></div>
                <div className="resize-handle resize-top-left"></div>
                <div className="resize-handle resize-top-right"></div>
                <div className="resize-handle resize-bottom-left"></div>
                <div className="resize-handle resize-bottom-right"></div>

                <div className="chat-header">
                    <div className="chat-header-content">
                        <div className="chat-title">
                            <h3>AI Assistant</h3>
                            <span className="status" data-state="idle" aria-live="polite">
                                <span className="status-dot"></span>
                                <span className="status-text">Offline</span>
                            </span>
                        </div>
                    </div>
                    <div className="chat-header-controls">
                        <button id="minimizeChat" className="control-button minimize-button" aria-label="Minimize chat" title="Minimize chat">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                        </button>
                        <button id="closeChat" className="close-button" aria-label="Close chat">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                </div>

                <div id="chatMessages" className="chat-messages" ref={chatMsgRef}>
                    <div className="message bot-message">
                        <div className="message-content">
                            <div className="message-text">Hello! I'm your AI assistant. How can I help you today?</div>
                            <span className="message-time">{getCurrentTime()}</span>
                        </div>
                    </div>
                    {
                        CheckIsEmptyArray(chatMsg) && visiblePrompts.length > 0 && (
                            <div className="suggested-prompts">
                                {visiblePrompts.map((s, i) => (
                                    <button
                                        key={`sugg-${i}`}
                                        type="button"
                                        className="suggested-prompt-chip"
                                        disabled={disableInput}
                                        onClick={() => sendSuggestedPrompt(s.prompt)}
                                    >
                                        {s.preview}
                                    </button>
                                ))}
                            </div>
                        )
                    }
                    {
                        !CheckIsEmptyArray(chatMsg) && chatMsg.map((m, i) => {
                            return (
                                <div key={i} id={m.messageId ? m.messageId : ''} className={`message ${m.isUser ? 'user-message' : 'bot-message'}`}>
                                    <div className='message-content'>
                                        {m.thinking && !m.isUser && !m.streaming && (() => {
                                            const isExpanded = expandedThinking.has(m.messageId);
                                            const tools = m.thinking.tools || [];
                                            const reasoningPhases = filterReasoningPhases(m.thinking.phases);
                                            return (
                                                <div className='thinking-section' onClick={() => toggleThinking(m.messageId)}>
                                                    <div className='thinking-toggle'>
                                                        <svg className={`thinking-chevron ${isExpanded ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="6 9 12 15 18 9"></polyline>
                                                        </svg>
                                                        <span className='thinking-summary'>
                                                            {`Thought for ${(m.thinking.duration / 1000).toFixed(1)}s`}
                                                            {tools.length > 0 && ` \u00B7 ${tools.length} action${tools.length === 1 ? '' : 's'}`}
                                                            {m.thinking.reasoningTokens > 0 && ` \u00B7 ${m.thinking.reasoningTokens} reasoning tokens`}
                                                        </span>
                                                    </div>
                                                    {isExpanded && (
                                                        <div className='action-trace' onClick={(e) => e.stopPropagation()}>
                                                            {reasoningPhases.map((phase, idx) => (
                                                                <PhaseItem
                                                                    key={`phase-${idx}`}
                                                                    phase={phase}
                                                                    sources={m.citations || []}
                                                                    detail={phase === 'Analyzing your question' && m.thinking.reasoningText ? m.thinking.reasoningText : null}
                                                                />
                                                            ))}
                                                            {(m.thinking.agentSteps || []).map((s, idx) => (
                                                                <PhaseItem
                                                                    key={`agent-${idx}`}
                                                                    phase={s.label}
                                                                    detail={s.text}
                                                                    markdown
                                                                />
                                                            ))}
                                                            {tools.map((tool, idx) => (
                                                                <ToolTraceItem key={`tool-${idx}`} tool={tool} />
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                        <div className='message-text'>
                                            {!m.streaming && (
                                                <button
                                                    type="button"
                                                    className={`msg-copy-btn${copiedId === (m.messageId || ('u' + i)) ? ' is-copied' : ''}`}
                                                    onClick={(e) => copyMessage(e, (m.messageId || ('u' + i)))}
                                                    title={copiedId === (m.messageId || ('u' + i)) ? 'Copied' : 'Copy'}
                                                    aria-label="Copy message"
                                                >
                                                    {copiedId === (m.messageId || ('u' + i)) ? (
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    ) : (
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                                                            <rect x="9" y="9" width="11" height="11" rx="2" />
                                                            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                                                        </svg>
                                                    )}
                                                </button>
                                            )}
                                            {m.attachments && m.attachments.length > 0 && (
                                                <div className='message-attachments'>
                                                    {m.attachments.map((att, idx) => (
                                                        att.type === 'image' ? (
                                                            <img key={idx} src={att.base64 || att.preview} alt={att.name} className='message-attachment-image' />
                                                        ) : (
                                                            <div key={idx} className='message-attachment-pdf'>
                                                                <span>📄</span> {att.name}
                                                            </div>
                                                        )
                                                    ))}
                                                </div>
                                            )}


                                            {m.kind === 'confirmation' && m.confirmation ? (
                                                <ConfirmationCard
                                                    key={(confirmationEdit && confirmationEdit.cardMessageId === m.messageId) ? ('edit-' + m.messageId) : ('view-' + m.messageId)}
                                                    payload={m.confirmation}
                                                    edit={m.messageId === (confirmationEdit && confirmationEdit.cardMessageId) ? confirmationEdit : null}
                                                    resolvedChoice={m.resolvedChoice}
                                                    onAccept={() => resolveConfirmation('accept')}
                                                    onKeep={() => startKeepEdit(m.messageId, m.confirmation)}
                                                    onReject={() => resolveConfirmation('reject')}
                                                    onApplyEdits={(items) => resolveConfirmation('keep', items)}
                                                    onCancelEdit={() => setConfirmationEdit(null)}
                                                />
                                            ) : m.isUser ? (
                                                <p style={{ whiteSpace: 'pre-wrap' }}>{m.content}</p>
                                            ) : m.streaming ? (() => {
                                                // nowTick (from the interval effect) drives re-renders so the
                                                // duration counter and chip rail stay live.
                                                void nowTick;
                                                const livePhases = currentTurnThinkingPhasesRef.current || [];
                                                const liveSecs = currentTurnStartedAtRef.current
                                                    ? Math.max(0, (Date.now() - currentTurnStartedAtRef.current) / 1000)
                                                    : null;
                                                return (
                                                    <div className="live-thinking">
                                                        <div className="live-thinking-aurora" aria-hidden="true">
                                                            <span className="aurora-blob aurora-blob--1" />
                                                            <span className="aurora-blob aurora-blob--2" />
                                                        </div>
                                                        <div className="live-thinking-shimmer" aria-hidden="true" />
                                                        <div className="live-thinking-header">
                                                            <span className="live-thinking-orb" aria-hidden="true" />
                                                            <span className="live-thinking-label">{thinkingPhase || 'Thinking'}</span>
                                                            {liveSecs !== null && (
                                                                <span className="live-thinking-duration">{liveSecs.toFixed(1)}s</span>
                                                            )}
                                                            {currentTurnToolHistoryRef.current.length > 0 && (
                                                                <span className="live-thinking-actions">
                                                                    {currentTurnToolHistoryRef.current.length} action{currentTurnToolHistoryRef.current.length === 1 ? '' : 's'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {m.content && m.content.trim() && (
                                                            <div className="live-thinking-text">{m.content}<span className="live-thinking-cursor" /></div>
                                                        )}
                                                        {livePhases.length > 0 && (
                                                            <div className="live-thinking-chips">
                                                                {livePhases.map((phase, idx) => {
                                                                    const isLive = idx === livePhases.length - 1;
                                                                    return (
                                                                        <span key={idx} className={`live-chip ${isLive ? 'is-live' : 'is-done'}`}>
                                                                            {isLive ? (
                                                                                <svg className="live-chip__icon" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                                                                    <circle cx="12" cy="12" r="5" />
                                                                                </svg>
                                                                            ) : (
                                                                                <svg className="live-chip__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                                                    <polyline points="20 6 9 17 4 12" />
                                                                                </svg>
                                                                            )}
                                                                            {phase}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })() : (
                                                (() => {
                                                    const blocks = splitMarkdownContent(m.content);

                                                    return blocks.map((block, blockIdx) => {
                                                        if (block.type === 'table') {
                                                            return (
                                                                <div className="chatbot-table-wrap" key={`table-wrap-${blockIdx}`}>
                                                                <table key={`table-${blockIdx}`}>
                                                                    <thead>
                                                                        <tr>
                                                                            {block.headers.map((header, headerIdx) => (
                                                                                <th key={`h-${blockIdx}-${headerIdx}`}>
                                                                                    <ReactMarkdown>{header}</ReactMarkdown>
                                                                                </th>
                                                                            ))}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {block.rows.map((row, rowIdx) => (
                                                                            <tr key={`r-${blockIdx}-${rowIdx}`}>
                                                                                {row.map((cell, cellIdx) => (
                                                                                    <td key={`c-${blockIdx}-${rowIdx}-${cellIdx}`}>
                                                                                        <ReactMarkdown>{cell}</ReactMarkdown>
                                                                                    </td>
                                                                                ))}
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <ReactMarkdown
                                                                key={`md-${blockIdx}`}
                                                                components={{
                                                                    p: ({ children }) => <p>{children}</p>,
                                                                    img: ({ src, alt }) => <img src={src} alt={alt || ''} className="da-chart-img" onClick={() => setLightbox({ kind: 'img', src })} />,
                                                                    strong: ({ children }) => <strong>{children}</strong>,
                                                                    em: ({ children }) => <em>{children}</em>,
                                                                    code: ({ inline, children, className, ...props }) => {
                                                                        const content = Array.isArray(children) ? children.join('') : children;
                                                                        if (inline) {
                                                                            return <code>{content}</code>;
                                                                        }
                                                                        if (className && className.indexOf('language-chart') !== -1) {
                                                                            return <ChartBlock content={content} openLightbox={openChartLightbox} />;
                                                                        }
                                                                        return (
                                                                            <pre className={className}>
                                                                                <code {...props}>{content}</code>
                                                                            </pre>
                                                                        );
                                                                    },
                                                                }}
                                                            >
                                                                {block.content}
                                                            </ReactMarkdown>
                                                        );
                                                    });
                                                })()
                                            )}
                                        </div>
                                        <div className='message-time'>{getCurrentTime()}</div>
                                        {
                                            !CheckIsEmptyArray(m.citations) && <div className='message-citations'>
                                                {
                                                    (() => {
                                                        const seen = new Set();
                                                        const unique = m.citations.filter(c => {
                                                            const key = `${c.source}|${c.page_no}`;
                                                            if (seen.has(key)) return false;
                                                            seen.add(key);
                                                            return true;
                                                        });
                                                        return unique.map((citation, index) => {
                                                            return (
                                                                <div
                                                                    id={`citation-${index}`}
                                                                    key={index}
                                                                    title={`Source: ${citation.source || 'Unknown'}${citation.page_no ? `, Page ${citation.page_no}` : ''}`}
                                                                    className={`citation${selectedCitation && selectedCitation._index === index ? ' active' : ''}`}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();

                                                                        setImageEnlarged(false);
                                                                        if (selectedCitation && selectedCitation._index === index) {
                                                                            setSelectedCitation(null);
                                                                            setShowSourcePanel(false);
                                                                        } else {
                                                                            setSelectedCitation({ ...citation, _index: index });
                                                                            setShowSourcePanel(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    {`[${index + 1}]`}
                                                                </div>
                                                            );
                                                        });
                                                    })()
                                                }
                                            </div>
                                        }
                                    </div>
                                </div>
                            );
                        })
                    }
                    {
                        showProcessing && <div id='processingIndicator' className='thinking-indicator'>
                            <div className='thinking-indicator-content'>
                                <div className='thinking-dots'><span></span><span></span><span></span></div>
                                <span className='thinking-phase-text'>{thinkingPhase || 'Processing'}</span>
                                {currentTurnToolHistoryRef.current.length > 0 && (
                                    <span className='thinking-actions-count'>
                                        {currentTurnToolHistoryRef.current.length} action{currentTurnToolHistoryRef.current.length === 1 ? '' : 's'}
                                    </span>
                                )}
                            </div>
                        </div>
                    }
                    {
                        showTyping && <div id='typingIndicator' className='typing-indicator'>
                            <div className='typing-dots'><span></span><span></span><span></span></div>
                        </div>
                    }
                    {
                        (errorMsg || '') !== '' && <div className='error-message'>
                            {`Error: ${errorMsg}`}
                        </div>
                    }
                </div>

                <div className="chat-input-container">
                    {attachments.length > 0 && (
                        <div className="attachment-preview-bar">
                            {attachments.map((att, index) => (
                                <div key={index} className="attachment-preview-item">
                                    {att.type === 'image' ? (
                                        <img src={att.preview} alt={att.name} className="attachment-preview-image" />
                                    ) : (
                                        <div className="attachment-preview-pdf">
                                            <span className="pdf-icon">📄</span>
                                            <span className="pdf-name">{att.name}</span>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="attachment-preview-remove"
                                        onClick={() => removeAttachment(index)}
                                        aria-label="Remove attachment"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <form
                        id="chatForm"
                        className="chat-form"
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp,.pdf"
                            multiple
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <button
                            type="button"
                            className="attachment-button"
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            disabled={disableInput}
                            aria-label="Attach file"
                            title="Attach image or PDF"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                            </svg>
                        </button>
                        <input
                            ref={inputRef}
                            disabled={disableInput}
                            type="text"
                            id="chatInput"
                            className="chat-input"
                            placeholder="Type your message..."
                            autoComplete="off"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSubmit(e);
                                }
                            }}
                        />
                        <button
                            onClick={(e) => handleSubmit(e)}
                            type="submit"
                            className="send-button"
                            aria-label="Send message"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="22" y1="2" x2="11" y2="13"></line>
                                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                            </svg>
                        </button>
                    </form>
                </div>
            </div>
            <div
                id="sourcePanel"
                className={`source-panel${showSourcePanel ? ' visible' : ''}`}
            >
                {selectedCitation && (
                    <>
                        <div className="source-panel-header">
                            <div className="source-panel-source-info">
                                {selectedCitation.source && (
                                    <div className="source-panel-source">{selectedCitation.source}</div>
                                )}
                                {selectedCitation.page_no && (
                                    <div className="source-panel-page">Page {selectedCitation.page_no}</div>
                                )}
                            </div>
                            <button
                                className="source-panel-close"
                                onClick={() => {
                                    setSelectedCitation(null);
                                    setShowSourcePanel(false);
                                    setImageEnlarged(false);
                                }}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="source-panel-body">
                            {selectedCitation.image_data ? (
                                <div className="source-panel-image-wrapper">
                                    <img
                                        src={`data:image/png;base64,${selectedCitation.image_data}`}
                                        alt={`Page ${selectedCitation.page_no || ''}`}
                                        className="source-panel-image"
                                    />
                                    <button
                                        className="source-panel-enlarge-btn"
                                        onClick={(e) => { e.stopPropagation(); setImageEnlarged(true); }}
                                        title="Enlarge image"
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </svg>
                                    </button>
                                </div>
                            ) : selectedCitation.text ? (
                                <div className="source-panel-text">
                                    {selectedCitation.text.length > 500
                                        ? selectedCitation.text.substring(0, 500) + '...'
                                        : selectedCitation.text
                                    }
                                </div>
                            ) : (
                                <div className="source-panel-placeholder">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                        <polyline points="21 15 16 10 5 21"></polyline>
                                    </svg>
                                    <span>No preview available</span>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
            {/* Enlarged image overlay */}
            {imageEnlarged && selectedCitation && selectedCitation.image_data && (
                <div className="image-enlarge-overlay" onClick={() => setImageEnlarged(false)}>
                    <div className="image-enlarge-content" onClick={(e) => e.stopPropagation()}>
                        <button className="image-enlarge-close" onClick={() => setImageEnlarged(false)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                        <img
                            src={`data:image/png;base64,${selectedCitation.image_data}`}
                            alt={`Page ${selectedCitation.page_no || ''}`}
                            className="image-enlarge-img"
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
// Log configuration (helpful for debugging)
if (CONFIG.isDevelopment) {
    console.log('%c=== LangGraph Chat Configuration ===', 'color: #00ff00; font-weight: bold');
    console.log('%cEnvironment:', 'color: #00ff00; font-weight: bold', 'Development');
    console.log('%cMode:', 'color: #00ff00; font-weight: bold', CONFIG.mode.toUpperCase());
    console.log('%cLangGraph URL:', 'color: #00ff00; font-weight: bold', CONFIG.LANGGRAPH_URL);
    console.log('%cAssistant ID:', 'color: #00ff00; font-weight: bold', CONFIG.ASSISTANT_ID);
    console.log('%c====================================', 'color: #00ff00; font-weight: bold');
}
